import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import _ from 'lodash'
import format from 'date-fns/format'
import addMonths from 'date-fns/add_months';
import addDays from 'date-fns/add_days';
import startOfWeek from 'date-fns/start_of_week';
import lastDayOfWeek from 'date-fns/last_day_of_week';
import subWeeks from 'date-fns/sub_weeks';
import {globalfunc} from '../globalfunc.js'
import { getUserAll,getTeamByCompany } from './core/cacheService';

const TEAMS_VIEW_CACHE_VERSION = 1
const TEAMS_VIEW_CACHE_PREFIX = 'happily:teams-view'

const getTeamsViewStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }
  return window.localStorage
}

const getTeamsViewCacheDay = () => format(new Date(), 'YYYY-MM-DD')

const getTeamsViewCacheStorageKey = (company: string) => {
  return `${TEAMS_VIEW_CACHE_PREFIX}:v${TEAMS_VIEW_CACHE_VERSION}:${company}`
}

const readTeamsViewCacheBundle = (company: string) => {
  if (!company) {
    return null
  }

  const storage = getTeamsViewStorage()
  if (!storage) {
    return null
  }

  const storageKey = getTeamsViewCacheStorageKey(company)
  try {
    const raw = storage.getItem(storageKey)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw)
    if (!parsed || parsed.version !== TEAMS_VIEW_CACHE_VERSION) {
      storage.removeItem(storageKey)
      return null
    }

    if (parsed.cacheDate !== getTeamsViewCacheDay() || typeof parsed.datasets !== 'object' || parsed.datasets === null) {
      storage.removeItem(storageKey)
      return null
    }

    return parsed
  } catch (error) {
    storage.removeItem(storageKey)
    return null
  }
}

const readTeamsViewCache = (company: string, datasetKey: string) => {
  const bundle = readTeamsViewCacheBundle(company)
  if (!bundle || bundle.datasets[datasetKey] === undefined) {
    return null
  }

  return _.cloneDeep(bundle.datasets[datasetKey])
}

const writeTeamsViewCache = (company: string, datasetKey: string, value: any) => {
  if (!company) {
    return
  }

  const storage = getTeamsViewStorage()
  if (!storage) {
    return
  }

  const storageKey = getTeamsViewCacheStorageKey(company)
  const existing = readTeamsViewCacheBundle(company)
  const bundle = existing || {
    version: TEAMS_VIEW_CACHE_VERSION,
    cacheDate: getTeamsViewCacheDay(),
    datasets: {}
  }

  bundle.version = TEAMS_VIEW_CACHE_VERSION
  bundle.cacheDate = getTeamsViewCacheDay()
  bundle.datasets[datasetKey] = _.cloneDeep(value)

  try {
    storage.setItem(storageKey, JSON.stringify(bundle))
  } catch (error) {
    // Ignore quota and serialization failures and fall back to live requests.
  }
}

const isLatestScopedRequest = (
  state: any,
  scope: 'main' | 'modal' | 'mainChart' | 'modalChart',
  requestKey: any
) => {
  if (requestKey === undefined || requestKey === null) {
    return true
  }

  if (scope === 'main') {
    return state.mainRequestKey === requestKey
  }

  if (scope === 'modal') {
    return state.modalRequestKey === requestKey
  }

  if (scope === 'mainChart') {
    return state.mainChartRequestKey === requestKey
  }

  return state.modalChartRequestKey === requestKey
}

const buildDefaultTeamScore = () => ({ score: 0, color: '#C7C7C7' })

const buildFallbackTeamTableRow = (state: any, teamData: any, teamId: string) => {
  const teamMeta = teamData && teamData[teamId] ? teamData[teamId] : null
  const impactRating = state.impactRating && state.impactRating[teamId]
    ? state.impactRating[teamId]
    : null

  return {
    id: teamId,
    team: teamMeta && teamMeta.name ? teamMeta.name : teamId,
    happirate: 0,
    riskrate: 0,
    satisfaction: buildDefaultTeamScore(),
    feedback: buildDefaultTeamScore(),
    wellness: buildDefaultTeamScore(),
    manager: buildDefaultTeamScore(),
    colleagues: buildDefaultTeamScore(),
    ambassadorship: buildDefaultTeamScore(),
    personal: buildDefaultTeamScore(),
    recognition: buildDefaultTeamScore(),
    alignment: buildDefaultTeamScore(),
    total: 0,
    avgimpact: impactRating ? impactRating.Avgimpact : '-',
    avgimpact_color: impactRating ? impactRating.avgimpact_color : 'red',
    retention_rate: impactRating ? impactRating.Retention_rate : '-',
    retention_color: impactRating ? impactRating.color : 'red',
    terminate: impactRating ? impactRating.terminated : 0,
    resigned: impactRating ? impactRating.resigned : 0,
    other: impactRating ? impactRating.other : 0,
    engagement_score: '-'
  }
}

const getTeamTreeRow = (state: any, teamData: any, teamId: string) => {
  if (!teamId) {
    return null
  }

  const existingRow = _.find(state.datalistall, { id: teamId })
  if (existingRow) {
    return _.cloneDeep(existingRow)
  }

  return buildFallbackTeamTableRow(state, teamData, teamId)
}

const getTopmostTeamId = (teamData: any) => {
  const teamAll: Array<any> = []
  const topmostArr: string[] = []

  for (const key in (teamData || {})) {
    const team = teamData[key]
    teamAll.push(team)
    if (!team.parent) {
      topmostArr.push(key)
    }
  }

  if (topmostArr.length === 0) {
    return ''
  }

  if (topmostArr.length === 1) {
    return topmostArr[0]
  }

  return (
    _.find(topmostArr, (value) => {
      const topmostChild = _.find(teamAll, { parent: value })
      return !!topmostChild
    }) || topmostArr[0]
  )
}

const buildTeamTreeBranch = async ({
  actionName,
  dispatch,
  state,
  teamData,
  teamId,
  type,
  requestKey
}: any) => {
  const organizationNode = state.organizations ? state.organizations[teamId] : null
  const row = getTeamTreeRow(state, teamData, teamId)

  if (!row) {
    return []
  }

  row.children = []
  if (type === 'parent') {
    row.depth = 0
    row.parent_id = null
    row.extended = 'extended'
  }

  const childNodes = _.values((organizationNode && organizationNode.parent) || {})

  for (const childNode of childNodes) {
    if (!childNode || !childNode.team_id) {
      continue
    }

    let childRow = getTeamTreeRow(state, teamData, childNode.team_id)
    const childOrganizationNode = state.organizations
      ? state.organizations[childNode.team_id]
      : null
    const hasNestedChildren = childNode.parent !== undefined || _.some(
      _.values((childOrganizationNode && childOrganizationNode.parent) || {}),
      (nestedChild: any) => nestedChild && nestedChild.team_id
    )
    if (!childRow) {
      continue
    }

    childRow.parent_id = teamId
    childRow.depth = (row.depth || 0) + 1

    if (hasNestedChildren) {
      const childResponse = await dispatch(actionName, {
        team: childNode.team_id,
        depth: childRow.depth,
        type: 'children',
        requestKey,
        teamData
      })

      childRow = childResponse && childResponse[0] ? childResponse[0] : childRow
    } else {
      delete childRow.children
    }

    childRow.child_num = Array.isArray(childRow.children)
      ? childRow.children.length
      : 0
    if (childRow.child_num === 0) {
      delete childRow.children
    }
    row.children.push(childRow)
  }

  row.child_num = row.children.length
  if (row.child_num === 0) {
    delete row.children
  }
  return [row]
}

export default {
    namespaced: true,
    state: {
      insight_teams: [],
      insight_teams_modal: [],
      datalistallLoadingStatus: true,
      datachart: [],
      datachartModal: [],
      selectmonth: [],
      selectmonthModal: [],
      organizations: [],
      insightteamsByComp: [],
      datalist: [],
      datalistModal: [],
      datalistall: [],
      team: '',
      sort: '0',
      sortModal: '0',
      teamHeight: '',
      teamHeightModal: '',
      question_category: [],
      mostengage:[],
      mostengageAll:[],
      mostengageRoot:[],
      mostengageModal:[],
      mostengageAllModal:[],
      mostengageRootModal:[],
      retentionrisks:[],
      retentionrisksAll:[],
      retentionrisksRoot:[],
      retentionrisksModal:[],
      retentionrisksAllModal:[],
      retentionrisksRootModal:[],
      mostimprove:[],
      mostimproveAll:[],
      mostimproveRoot:[],
      mostimproveModal:[],
      mostimproveAllModal:[],
      mostimproveRootModal:[],
      mostcritical:[],
      mostcriticalAll:[],
      mostcriticalRoot:[],
      mostcriticalModal:[],
      mostcriticalAllModal:[],
      mostcriticalRootModal:[],
      teamArr:[],
      showSnackbar: false,
      teamAll:[],
      organization1level: {},
      loadingData: true,
      loadingDataModal: true,
      impactRating:[],
      loadingDataEngagement: true,
      loadingDataEngagementModal: true,
      databarchartEngagementScore: {},
      databarchartEngagementScoreModal: {},
      dataEngagementScoreHistoryByTeam: [],
      dataMasterLabel: [],
      optColor: { 'yellow': 'yellow', 'green': 'green', 'red': 'red'},
      // Cached weekly active users (13-week window) for Team Health Overview
      overviewWeeklyActive: { activeuser: [], alluser: [] },
      // Cached weekly happiness and risk (13-week window) for Team Health Overview
      overviewWeeklyHealth: { happy: [], risk: [] },
      dimensionTrendsModal: {},
      loadingDimensionTrends: false,
      mainRequestKey: 0,
      modalRequestKey: 0,
      mainChartRequestKey: 0,
      modalChartRequestKey: 0
    },
    mutations: {
      setDatalistallLoadingStatus(state: any, value: boolean) {
        state.datalistallLoadingStatus = value
      },
      setTeamArr(state:any, result:any) {
        state.teamArr = result
      },
      setInsightTeams(state: any, result: any) {
        state.insight_teams = result
      },
      setInsightTeamsModal(state: any, result: any) {
        state.insight_teams_modal = result
      },
      setChartHappyRisk(state: any, result: any) {
        state.datachart = result
      },
      setChartHappyRiskModal(state: any, result: any) {
        state.datachartModal = result
      },
      setSelectMonth(state: any, value: any) {
        state.selectmonth = value
      },
      setSelectMonthModal(state: any, value: any) {
        state.selectmonthModal = value
      },
      setOrganizations(state: any, value: any) {
        state.organizations = value
      },
      setInsightTeamsByComp(state: any, value: any) {
        state.insightteamsByComp = value
      },
      setOverviewWeeklyActive(state: any, value: any) {
        state.overviewWeeklyActive = value || { activeuser: [], alluser: [] }
      },
      setOverviewWeeklyHealth(state: any, value: any) {
        state.overviewWeeklyHealth = value || { happy: [], risk: [] }
      },
      setTeam(state: any, value: any) {
        state.team = value
      },
      setTeamModal(state: any, value: any) {
        state.teammodal = value
      },
      setDataTable(state: any, value: any) {
        state.datalist = value
      },
      setDataTableModal(state: any, value: any) {
        state.datalistModal = value
      },
      setDataTableAll(state: any, value: any) {
        state.datalistall = value
      },
      setSort(state: any, value: any) {
        state.sort = value
      },
      setSortModal(state: any, value: any) {
        state.sortModal = value
      },
      setTeamHeight(state: any, value: any) {
        state.teamHeight = value
      },
      setTeamHeightModal(state: any, value: any) {
        state.teamHeightModal = value
      },
      setDimensionTrendsModal(state: any, value: any) {
        state.dimensionTrendsModal = value || {}
      },
      setLoadingDimensionTrends(state: any, value: boolean) {
        state.loadingDimensionTrends = value
      },
      setMainRequestKey(state: any, value: number) {
        state.mainRequestKey = value
      },
      setModalRequestKey(state: any, value: number) {
        state.modalRequestKey = value
      },
      setMainChartRequestKey(state: any, value: number) {
        state.mainChartRequestKey = value
      },
      setModalChartRequestKey(state: any, value: number) {
        state.modalChartRequestKey = value
      },
      setQuestionCategory(state: any, value: any) {
        state.question_category = value
      },
      setMostEngageCheck(state:any, result:Array<any>) {
        let num=1
        let temp_result:Array<any> = []
        Object.entries(result).forEach(([key, value]) =>{
            if(value['score'] >= 0){
                _.set(value,'no', num++);
                _.set(temp_result,key,value);
            } 
        });
        state.mostengage = temp_result
      },
      setMostEngageCheckModal(state:any, result:Array<any>) {
        let num=1
        let temp_result:Array<any> = []
        Object.entries(result).forEach(([key, value]) =>{
            if(value['score'] >= 0){
                _.set(value,'no', num++);
                _.set(temp_result,key,value);
            } 
        });
        state.mostengageModal = temp_result
      },
      setMostEngageAll(state:any, result:any) {
        state.mostengageAll = result
      },
      setMostEngageRoot(state:any, result:any) {
          state.mostengageRoot = result
      },
      setRetentionRisksCheck(state:any, result:Array<any>) {
        let num=1
        let temp_result:Array<any> = []
        Object.entries(result).forEach(([key, value]) =>{
            if(value['score'] <= 0){
                _.set(value,'no', num++);
                _.set(temp_result,key,value);
            } 
        });
        state.retentionrisks = temp_result
      },
      setRetentionRisksCheckModal(state:any, result:Array<any>) {
        let num=1
        let temp_result:Array<any> = []
        Object.entries(result).forEach(([key, value]) =>{
            if(value['score'] <= 0){
                _.set(value,'no', num++);
                _.set(temp_result,key,value);
            } 
        });
        state.retentionrisksModal = temp_result
      },
      setRetentionRisksAll(state:any, result:any) {
        state.retentionrisksAll = result
      },
      setRetentionRisksRoot(state:any, result:any) {
          state.retentionrisksRoot = result
      },
      setMostImproveCheck(state:any, result:Array<any>) {
          let num=1
          let temp_result:Array<any> = []
          Object.entries(result).forEach(([key, value]) =>{
              if(value['score'] > 0){
                  _.set(value,'no', num++);
                  _.set(temp_result,key,value);
              } 
          });
          state.mostimprove = temp_result
      },
      setMostImproveCheckModal(state:any, result:Array<any>) {
        let num=1
        let temp_result:Array<any> = []
        Object.entries(result).forEach(([key, value]) =>{
            if(value['score'] > 0){
                _.set(value,'no', num++);
                _.set(temp_result,key,value);
            } 
        });
        state.mostimproveModal = temp_result
    },
      setMostImproveAll(state:any, result:any) {
          state.mostimproveAll = result
      },
      setMostImproveRoot(state:any, result:any) {
          state.mostimproveRoot = result
      },
      setMostCritical(state:any, result:Array<any>) {
        let num=1
        Object.entries(result).forEach(([key, value]) =>{ 
            _.set(value,'no', num++);
        });
        state.mostcritical = result
      },
      setMostCriticalCheck(state:any, result:Array<any>) {
          let num=1
          let temp_result:Array<any> = []
          Object.entries(result).forEach(([key, value]) =>{
              if(value['score'] < 0){
                  _.set(value,'no', num++);
                  _.set(temp_result,key,value);
              } 
          });
          state.mostcritical = temp_result
      },
      setMostCriticalCheckModal(state:any, result:Array<any>) {
        let num=1
        let temp_result:Array<any> = []
        Object.entries(result).forEach(([key, value]) =>{
            if(value['score'] < 0){
                _.set(value,'no', num++);
                _.set(temp_result,key,value);
            } 
        });
        state.mostcriticalModal = temp_result
    },
      setMostCriticalAll(state:any, result:any) {
          state.mostcriticalAll = result
      },
      setMostCriticalRoot(state:any, result:any) {
          state.mostcriticalRoot = result
      },
      setSnackbar(state: any, value: any) {
        state.showSnackbar = value
      },
      setTeamAll(state: any, value: any) {
        state.teamAll = value
      },
      setOrganization1level(state: any, value: any) {
        state.organization1level = value
      },
      setLoadingData(state: any, value: any) {
        state.loadingData = value
      },
      setLoadingDataModal(state: any, value: any) {
        state.loadingDataModal = value
      },
      setLoadingDataEngagement(state: any, value: any) {
        state.loadingDataEngagement = value
      },
      setChartEngagementScore(state: any, result: any) {
        state.databarchartEngagementScore = result
      },
      setLoadingDataEngagementModal(state: any, value: any) {
        state.loadingDataEngagementModal = value
      },
      setChartEngagementScoreModal(state: any, result: any) {
        state.databarchartEngagementScoreModal = result
      },
      setImpactRating(state: any, value: any) {
        state.impactRating = value
      },
    },
    actions: {
      async getTeamArr({commit,dispatch,rootState}:any, user:any){
        const organization1level = {}
        if(user.user_type == 'leadership'){
          if(user.email){
            const emailKey = user.email.replace(/\./g, '||')
            var userData = rootState.main.allUser[emailKey]//(await firebase.database().ref(`/users/${emailKey}`).once('value')).val()
          }
          if(userData){
                await firebase.firestore().collection('organization1level').where("company", "==", userData.company).where("team_id","==",userData.team).get()
                .then((querySnapshot)=> {
                    if(querySnapshot.empty){
                        organization1level[userData.team] = true
                    }else{
                        querySnapshot.forEach((doc)=> {
                            _.forEach(doc.data().parent, function(_,key) {
                                organization1level[key] = true
                            });
                        });
                    }
                    //console.log( 'organization1level',organization1level)
                    commit('setOrganization1level', organization1level)
                });
            }
        }
        var tempData = {};
        var teamAll : Array<any> = [];
        const teamData = await getTeamByCompany(user.company)
        for(const key in teamData){
          const team = teamData[key]
          team.key = key
          tempData[team.key] = team
          teamAll.push(team)
        }
        commit('setTeamArr', tempData)
        commit('setTeamAll', teamAll)
        dispatch('getMostEngage', user)
        dispatch('getRetentionRisks', user)
        dispatch('getMostImprove', user)
      

        // firebase.database().ref().child('team').orderByChild('company').equalTo(`${user.company}`).once('value', snapshot => {
        //     var tempData = {};
        //     var teamAll : Array<any> = [];
        //     snapshot.forEach((doc):any => {
        //         const team = doc.val()
        //         team.key = doc.key
        //         tempData[team.key] = team
        //         teamAll.push(team)
        //     });
         
        //     commit('setTeamArr', tempData)
        //     commit('setTeamAll', teamAll)
        //     dispatch('getMostEngage', user)
        //     dispatch('getRetentionRisks', user)
        //     dispatch('getMostImprove', user)
        // }).catch(error => {
        //     console.log(error)
        // });
      },
      async getTeamOrganization1level({commit,dispatch,rootState}:any, user:any){
        const organization1level = {}
        await dispatch('main/getteam',true,{root:true})
        const users = await getUserAll(user.company)
        const team = await getTeamByCompany(user.company)
        const extitUser={}
        await firebase.database().ref().child('users_employee_exit').orderByChild('company').equalTo(`${user.company}`).once('value', snapshot => {
          snapshot.forEach((doc):any => {
            const data = doc.val()
            data.key = doc.key
            let exit_date = ''
            if(data.exitdated){
              exit_date = data.exitdated
            }else if(data.archive_date){
              exit_date = data.archive_date
            }else if(data.archived_timestamp){
              exit_date = data.archived_timestamp
            }
            if(exit_date){
              const archive_date =format(new Date(exit_date), 'YYYY-MM-DD')
          
              // Only count archived employees from the last 6 months for retention calculation
              if(archive_date>=format(addMonths(new Date(), -6), 'YYYY-MM-DD')){
                if(!extitUser[data.team]){
                  extitUser[data.team]={count:0,terminated:{},resigned:{},other:{}}
                }
                extitUser[data.team].count++
                if(!extitUser[data.team].terminated[format(new Date(exit_date), 'YYYY-MM')]){
                  extitUser[data.team].terminated[format(new Date(exit_date), 'YYYY-MM')]=0
                }
  
                if(!extitUser[data.team].other[format(new Date(exit_date), 'YYYY-MM')]){
                  extitUser[data.team].other[format(new Date(exit_date), 'YYYY-MM')]=0
                }
  
                if(!extitUser[data.team].resigned[format(new Date(exit_date), 'YYYY-MM')]){
                  extitUser[data.team].resigned[format(new Date(exit_date), 'YYYY-MM')]=0
                }
  
                
                if(data.archive_type=='terminated'){
                  extitUser[data.team].terminated[format(new Date(exit_date), 'YYYY-MM')]++
                }else if(data.archive_type=='other'){
                  extitUser[data.team].other[format(new Date(exit_date), 'YYYY-MM')]++
                }else{
                  extitUser[data.team].resigned[format(new Date(exit_date), 'YYYY-MM')]++
                }
              }
            }
          });
      })
        await firebase.firestore().collection('organization1level').where("company", "==", user.company).get()
        .then((querySnapshot)=> {
                querySnapshot.forEach((doc)=> {
                  const team_id=doc.data().team_id
                    for(const key in doc.data().parent){
    
                      if(!organization1level[team_id]){
                        organization1level[team_id]={impact_rating:0,userCount:0,extitUser:0,num_employees_beginning:0,terminated:{},resigned:{},other:{}}
                      } 
                        if(extitUser[key]){
                          organization1level[team_id].extitUser=organization1level[team_id].extitUser+extitUser[key].count
                          for(let i=5;i>=0;i--){
                            const dateKey=format(addMonths(new Date(), -i), 'YYYY-MM')
                            if(!organization1level[team_id].terminated[dateKey]){
                              organization1level[team_id].terminated[dateKey]=0
                            }
                            if(!organization1level[team_id].resigned[dateKey]){
                              organization1level[team_id].resigned[dateKey]=0
                            }
                            if(!organization1level[team_id].other[dateKey]){
                              organization1level[team_id].other[dateKey]=0
                            }
                            
                            organization1level[team_id].terminated[dateKey]+=extitUser[key].terminated[dateKey]?extitUser[key].terminated[dateKey]:0
                            organization1level[team_id].resigned[dateKey]+=extitUser[key].resigned[dateKey]?extitUser[key].resigned[dateKey]:0
                            organization1level[team_id].other[dateKey]+=extitUser[key].other[dateKey]?extitUser[key].other[dateKey]:0

                          }

                        }
                        const fileterUser= _.filter(users, function(o) { return o.team == key; });
                        for(const keyUser in fileterUser){
                          
                          const impact_rating=fileterUser[keyUser].impact_rating?fileterUser[keyUser].impact_rating:0
                          organization1level[team_id].impact_rating=organization1level[team_id].impact_rating+parseFloat(impact_rating)

                          // Count all current employees for baseline calculation
                          organization1level[team_id].num_employees_beginning++
                        } 
                        
                        organization1level[team_id].userCount=organization1level[team_id].userCount+fileterUser.length
                    }
                });
            for(const teamkey in team){
              if(!organization1level[teamkey]){

                if(!organization1level[teamkey]){
                  organization1level[teamkey]={impact_rating:0,userCount:0,extitUser:0,num_employees_beginning:0,terminated:{},resigned:{},other:{}}
                }
                if(extitUser[teamkey]){
                  organization1level[teamkey].extitUser=organization1level[teamkey].extitUser+extitUser[teamkey].count
                  for(let i=5;i>=0;i--){
                    const dateKey=format(addMonths(new Date(), -i), 'YYYY-MM')
                    if(!organization1level[teamkey].terminated[dateKey]){
                      organization1level[teamkey].terminated[dateKey]=0
                    }
                    if(!organization1level[teamkey].resigned[dateKey]){
                      organization1level[teamkey].resigned[dateKey]=0
                    }
                    if(!organization1level[teamkey].other[dateKey]){
                      organization1level[teamkey].other[dateKey]=0
                    }


                    organization1level[teamkey].terminated[dateKey]+=extitUser[teamkey].terminated[dateKey]?extitUser[teamkey].terminated[dateKey]:0
                    organization1level[teamkey].resigned[dateKey]+=extitUser[teamkey].resigned[dateKey]?extitUser[teamkey].resigned[dateKey]:0
                    organization1level[teamkey].other[dateKey]+=extitUser[teamkey].other[dateKey]?extitUser[teamkey].other[dateKey]:0
                  }                
                }
                
                const fileterUser=_.filter(users, function(o) { return o.team == teamkey; });
                
                for(const keyUser in fileterUser){
                  
                  const impact_rating=fileterUser[keyUser].impact_rating?fileterUser[keyUser].impact_rating:0
                  organization1level[teamkey].impact_rating=organization1level[teamkey].impact_rating+parseFloat(impact_rating)
                  // Count all current employees for baseline calculation
                  organization1level[teamkey].num_employees_beginning++
                } 
                
                // Calculate retention rate: (current_employees) / (current_employees + archived_employees_in_6_months) * 100
                // This represents the percentage of the team that is still employed
                const totalTeamSize = organization1level[teamkey].num_employees_beginning + organization1level[teamkey].extitUser
                organization1level[teamkey].userCount=organization1level[teamkey].userCount+fileterUser.length
                organization1level[teamkey].Retention_rate = totalTeamSize > 0 ? ((organization1level[teamkey].num_employees_beginning / totalTeamSize) * 100).toFixed(1) : 0
                organization1level[teamkey].Avgimpact=isNaN(organization1level[teamkey].impact_rating/organization1level[teamkey].userCount)?0:(organization1level[teamkey].impact_rating/organization1level[teamkey].userCount).toFixed(1)
                
              }else{
                // Calculate retention rate for organization level teams
                const totalTeamSize = organization1level[teamkey].num_employees_beginning + organization1level[teamkey].extitUser
                organization1level[teamkey].Retention_rate = totalTeamSize > 0 ? ((organization1level[teamkey].num_employees_beginning / totalTeamSize) * 100).toFixed(1) : 0
                organization1level[teamkey].Avgimpact=isNaN(organization1level[teamkey].impact_rating/organization1level[teamkey].userCount)?0:(organization1level[teamkey].impact_rating/organization1level[teamkey].userCount).toFixed(1)

             }
              if(organization1level[teamkey].Retention_rate>=90){
                organization1level[teamkey].color="green"
              }else if(organization1level[teamkey].Retention_rate>=80){
                organization1level[teamkey].color="yellow"
              }else {
                organization1level[teamkey].color="red"
              }

              if(organization1level[teamkey].Avgimpact>=70){
                organization1level[teamkey].avgimpact_color="green"
              }else if(organization1level[teamkey].Avgimpact>=40){
                organization1level[teamkey].avgimpact_color="yellow"
              }else {
                organization1level[teamkey].avgimpact_color="red"
              }
            
          }
            //console.log("-L49aOVh-R8ua4EILUmm",organization1level['-L49aOVh-R8ua4EILUmm'])
            commit('setImpactRating', organization1level)
        });
      },

      async getInsightTeams({ commit, rootState, state }: any, {selectTeam, requestKey}: any) {
        // const allusers = (await firebase.database().ref('users').orderByChild("company").equalTo(rootState.authen.user.company).once('value')).val()
        commit('setLoadingData',true)
        if (!selectTeam) {
          if(!state.teamHeight){
            if(rootState.team.teamsall.length >0){
              let topmostArr: Array<any> = [];
              _.forEach(rootState.team.teamsall, (value)=> {
                if(value.parent == ''){
                  topmostArr.push(value.key)
                }
              });
              selectTeam = topmostArr[0]
              if(topmostArr.length > 1){
                selectTeam = _.find(topmostArr, (value) => {
                  const chk_topmost = _.find(rootState.team.teamsall, { parent: value });
                  return !!chk_topmost;
                });
              }
              commit('setTeamHeight', selectTeam)
            }else{
              let topmostArr: Array<any> = [];
              let teamAll: Array<any> = [];
              const teamData = await getTeamByCompany(rootState.authen.user.company)
              for(const key in teamData){
                teamAll.push(teamData[key])
                if (!teamData[key].parent){
                  topmostArr.push(key)
                }
               
              }
              selectTeam = topmostArr[0]
                if(topmostArr.length > 1){
                  selectTeam = _.find(topmostArr, (value) => {
                    const chk_topmost = _.find(teamAll, { parent: value });
                    return !!chk_topmost;
                  });
                }
                commit('setTeamHeight', selectTeam)
              // await firebase.database().ref('team').orderByChild('company').equalTo(`${rootState.authen.user.company}`).once('value', snapshot => {
              //   snapshot.forEach((doc): any => {
              //     teamAll.push(doc.val())
              //     if (!doc.val().parent){
              //       topmostArr.push(doc.key)
              //     }
              //   });
              //   selectTeam = topmostArr[0]
              //   if(topmostArr.length > 1){
              //     selectTeam = _.find(topmostArr, (value) => {
              //       const chk_topmost = _.find(teamAll, { parent: value });
              //       return !!chk_topmost;
              //     });
              //   }
              //   commit('setTeamHeight', selectTeam)
              // });
            }
          }else{
            selectTeam = state.teamHeight
          }
        }
      //   firebase.firestore().collection(`active_users/${rootState.authen.user.company}-2020-09/users`).get()
      //     .then(function (querySnapshot) {
      //     const membersusers: Array<any> = [];
      //     const membersmonth: Array<any> = [];
      //     querySnapshot.forEach((doc) => {
      //         const member = doc.data()
      //         member.key = doc.id
      //         const emailKey = doc.data().email.replace(/\./g, '||');
      //         membersusers[member.email] = true
      //         membersmonth.push(member)
      //         // if(allusers[emailKey]){
      //         //     member.image = allusers[emailKey].profilepic_crop
      //         //     membersmonth.push(member)
      //         // }else{
      //         //   console.log(member)
      //         // }
      //     });
      //     console.log("month ==> "+membersmonth.length)
      //   firebase.firestore().collection(`active_users_teams`).where("company", "==", rootState.authen.user.company).where("team", "==", selectTeam).where("date", ">=", '2020-09-01').where("date", "<=", '2020-09-09').get()
      //   .then(function (querySnapshot) {
      //   const members: Array<any> = [];
      //   const membersout: Array<any> = [];
      //   const memberteam: Array<any> = [];
      //   querySnapshot.forEach((doc) => {
      //       const data = doc.data()
      //       _.forEach(data.users, (value,key)=> {
      //         const emailKey = value.replace(/\./g, '||');
      //         if(!membersusers[value]){
      //           console.log(value)
      //         }
      //         if(allusers[emailKey]){
      //           members[value] = true
      //         }else{
      //           membersout[value] = true
      //         }
      //         memberteam[value] = true
      //       });
      //   });
      //   Object.entries(membersusers).forEach(([key, value]) =>{ 
      //     if(!memberteam[key]){
      //       console.log(membersusers[key])
      //       console.log(key)
      //     }
      //   });
      //   console.log(Object.keys(members).length)
      //   console.log(Object.keys(membersout).length)
      //   console.log(membersout)
      //   });
      // });
        const insight_teams: Array<any> = [];
        const querySnapshot = await firebase.firestore()
          .collection('insight_teams')
          .where("quest_id", "==", '-Kgs61AHOnpJ3rfyQr6t')
          .where("team", "==", selectTeam)
          .where("date", ">=", format(addMonths(new Date(), -24), 'YYYY-MM-DD'))
          .where("date", "<=", format(new Date(), 'YYYY-MM-DD'))
          .orderBy('date', 'asc')
          .get()

        querySnapshot.forEach((doc) => {
          const insight_team = doc.data()
          insight_team.key = doc.id
          insight_teams.push(insight_team)
        })

        if (!isLatestScopedRequest(state, 'main', requestKey)) {
          return insight_teams
        }

        commit('setInsightTeams', insight_teams)
        return insight_teams
      },
      async getInsightTeamsModal({ commit, rootState, state }: any, {selectTeam, requestKey}: any) {
        if (!selectTeam) {
          if(!state.teamHeightModal){
            if(rootState.team.teamsall.length >0){
              let topmostArr: Array<any> = [];
              _.forEach(rootState.team.teamsall, (value)=> {
                if(value.parent == ''){
                  topmostArr.push(value.key)
                }
              });
              selectTeam = topmostArr[0]
              if(topmostArr.length > 1){
                selectTeam = _.find(topmostArr, (value) => {
                  const chk_topmost = _.find(rootState.team.teamsall, { parent: value });
                  return !!chk_topmost;
                });
              }
              commit('setTeamHeightModal', selectTeam)
            }else{
              let topmostArr: Array<any> = [];
              let teamAll: Array<any> = [];
              const teamData = await getTeamByCompany(rootState.authen.user.company)
              for(const key in teamData){
                teamAll.push(teamData[key])
                if (!teamData[key].parent){
                  topmostArr.push(key)
                }
              }
              selectTeam = topmostArr[0]
              if(topmostArr.length > 1){
                selectTeam = _.find(topmostArr, (value) => {
                  const chk_topmost = _.find(teamAll, { parent: value });
                  return !!chk_topmost;
                });
              }
              commit('teamHeightModal', selectTeam)
              // await firebase.database().ref('team').orderByChild('company').equalTo(`${rootState.authen.user.company}`).once('value', snapshot => {
              //   snapshot.forEach((doc): any => {
              //     teamAll.push(doc.val())
              //     if (!doc.val().parent){
              //       topmostArr.push(doc.key)
              //     }
              //   });
              //   selectTeam = topmostArr[0]
              //   if(topmostArr.length > 1){
              //     selectTeam = _.find(topmostArr, (value) => {
              //       const chk_topmost = _.find(teamAll, { parent: value });
              //       return !!chk_topmost;
              //     });
              //   }
              //   commit('teamHeightModal', selectTeam)
              // });
            }
          }else{
            selectTeam = state.teamHeightModal
          }
        }
        const insight_teams: Array<any> = [];
        const querySnapshot = await firebase.firestore()
          .collection('insight_teams')
          .where("quest_id", "==", '-Kgs61AHOnpJ3rfyQr6t')
          .where("team", "==", selectTeam)
          .where("date", ">=", format(addMonths(new Date(), -24), 'YYYY-MM-DD'))
          .where("date", "<=", format(new Date(), 'YYYY-MM-DD'))
          .orderBy('date', 'asc')
          .get()

        querySnapshot.forEach((doc) => {
          const insight_team = doc.data()
          insight_team.key = doc.id
          insight_teams.push(insight_team)
        })

        if (!isLatestScopedRequest(state, 'modal', requestKey)) {
          return insight_teams
        }

        commit('setInsightTeamsModal', insight_teams)
        return insight_teams
      },
      async searchSpotlightByTeamModal({commit,dispatch, state}:any, selectTeam: any){
        if(selectTeam){
          const organizations = state.organizations
          let teams = organizations[selectTeam]
          if(!teams){
            let parentTeam = (state.teamArr[selectTeam].parent != "" ? state.teamArr[ state.teamArr[selectTeam].parent ].key : "")
            selectTeam = parentTeam
          }
        }
        dispatch('reponseSearchAll', selectTeam).then((result) => {
            commit('setMostEngageCheckModal', result[0])
            commit('setRetentionRisksCheckModal',result[1])
            commit('setMostImproveCheckModal',result[2])
            commit('setMostCriticalCheckModal',result[3])
        });
      },
      async searchSpotlightByTeam({commit,dispatch, state}:any, payload: any){
        const selectTeamValue = payload && typeof payload === 'object'
          ? payload.selectTeam
          : payload
        const requestKey = payload && typeof payload === 'object'
          ? payload.requestKey
          : undefined
        let selectTeam = selectTeamValue
        if(selectTeam){
          const organizations = state.organizations
          let teams = organizations[selectTeam]
          if(!teams){
            let parentTeam = (state.teamArr[selectTeam].parent != "" ? state.teamArr[ state.teamArr[selectTeam].parent ].key : "")
            selectTeam = parentTeam
          }
        }
        const result = await dispatch('reponseSearchAll', selectTeam)
        if (!isLatestScopedRequest(state, 'main', requestKey)) {
          return result
        }

        commit('setMostEngageCheck', result[0])
        commit('setRetentionRisksCheck',result[1])
        commit('setMostImproveCheck',result[2])
        commit('setMostCriticalCheck',result[3])
        return result
      },
      async reponseSearchAll({state}:any, selectTeam: any){
        const data:Array<any> = []
        let list_MostEngage:Array<any> = []
        let list_RetentionRisks:Array<any> = []
        let list_MostImprove:Array<any> = []
        let list_MostCritical:Array<any> = []
        if(selectTeam && selectTeam.substring(0, 1) =='-'){
            // type : most_engaged
            if(state.mostengageRoot[selectTeam] !== undefined){
                list_MostEngage = _.clone(state.mostengageRoot[selectTeam]);
            }else{
                list_MostEngage = _.filter(state.mostengageAll, (item):any => {
                    if (item.teamid.match(selectTeam)){
                        return true
                    }
                });
            }
            // type : retention_risk
            if(state.retentionrisksRoot[selectTeam] !== undefined){
                list_RetentionRisks = _.clone(state.retentionrisksRoot[selectTeam]);
            }else{
                list_RetentionRisks = _.filter(state.retentionrisksAll, (item):any => {
                    if (item.teamid.match(selectTeam)){
                        return true
                    }
                });
            }
            // type : most_improved
            if(state.mostimproveRoot[selectTeam] !== undefined){
                list_MostImprove = _.clone(state.mostimproveRoot[selectTeam]);
            }else{
                list_MostImprove = _.filter(state.mostimproveAll, (item):any => {
                    if (item.teamid.match(selectTeam)){
                        return true
                    }
                });
            }
            // type : most_critical
            if(state.mostcriticalRoot[selectTeam] !== undefined){
                list_MostCritical = _.clone(state.mostcriticalRoot[selectTeam]);
            }else{
                list_MostCritical = _.filter(state.mostcriticalAll, (item):any => {
                    if (item.teamid.match(selectTeam)){
                        return true
                    }
                });
            }
     
        }else{
            list_MostEngage = state.mostengageAll
            list_RetentionRisks = state.retentionrisksAll
            list_MostImprove = state.mostimproveAll
            list_MostCritical = state.mostcriticalAll
        }
        if(state.inputSearch){
            var re = new RegExp(state.inputSearch, 'gi')
            list_MostEngage = _.filter(list_MostEngage, (item):any => {
                if (item.team.match(re)){
                    return true
                }
            });
            list_RetentionRisks = _.filter(list_RetentionRisks, (item):any => {
                if (item.team.match(re)){
                    return true
                }
            });
            list_MostImprove = _.filter(list_MostImprove, (item):any => {
                if (item.team.match(re)){
                    return true
                }
            });
            list_MostCritical = _.filter(list_MostCritical, (item):any => {
                if (item.team.match(re)){
                    return true
                }
            });
        }
        data.push(list_MostEngage.slice(0, 2))
        data.push(list_RetentionRisks.slice(0, 2))
        data.push(list_MostImprove.slice(0, 2))
        data.push(list_MostCritical.slice(0, 2))
        return data
      },
      async getInsightTeamsByComp({ commit, dispatch, rootState, state }: any) {
        const company = rootState.authen.user.company
        commit('setDatalistallLoadingStatus', true)
        let questions: { [key: string]: string } = {};
        if(state.question_category.length == 0){
          if(rootState.question.questionsall.length>0){
            _.forEach(rootState.question.questionsall, (value,key)=> {
              questions[value.key] = value.category
            });
          }else{
            await firebase.database().ref('questions').orderByChild('status').equalTo('Y').once('value', snapshot => {
              snapshot.forEach((doc): any => {
                const question = doc.val()
                question.key = doc.key
                questions[question.key] = question.category
              });
            });
          }
          commit('setQuestionCategory', questions)
        }else{
          questions = state.question_category
        }

        const cachedInsightTeamsByComp = readTeamsViewCache(company, 'insightTeamsByComp')
        if (cachedInsightTeamsByComp) {
          commit('setInsightTeamsByComp', cachedInsightTeamsByComp)
          if (state.team) {
            dispatch('getDataTable', state.team)
          }
          commit('setDatalistallLoadingStatus', false)
          return cachedInsightTeamsByComp
        }

        const insight_teams: Array<any> = [];
        const querySnapshot = await firebase.firestore()
          .collection('insight_teams_fix')
          .where("company", "==", company)
          .get()

        querySnapshot.forEach(function (doc) {
          const insight_team = doc.data()
          if(insight_team.cohort == undefined){
            insight_team.key = doc.id
            insight_team.category = questions[insight_team.quest_id]
            insight_teams.push(insight_team)
          }
        })

        const sortedInsightTeams = _.sortBy(insight_teams, [function (o: any) { return o.team; }])
        writeTeamsViewCache(company, 'insightTeamsByComp', sortedInsightTeams)
        commit('setInsightTeamsByComp', sortedInsightTeams)
        if (state.team) {
          await dispatch('getDataTable', state.team)
        }
        commit('setDatalistallLoadingStatus', false)
        return sortedInsightTeams
      },
      async getOrganization({ commit,state, rootState }: any) {
        if(Object.keys(state.organizations || {}).length == 0){
          const querySnapshot = await firebase.firestore()
            .collection('organization')
            .where("company", "==", rootState.authen.user.company)
            .get()

          const organizations: Array<any> = [];
          querySnapshot.forEach((doc) => {
            const organization = doc.data()
            organization.key = doc.id
            organizations[organization.team_id] = organization
          });
          commit('setOrganizations', organizations)
          return organizations
        }
        return state.organizations
      },
      getParent({ dispatch, state }: any, value: string){
        const organizations = state.organizations
        const out: Array<any> = [];
        for (var i in organizations) {
          if (organizations[i].parent == parent) {
            const children = dispatch('getParent', organizations[i].id)

            if (children.length) {
              organizations[i].children = children
            }
            out.push(organizations[i])
          }
        }
        return out
      },
      async getDataChart({ commit, state, rootState }: any, {value,activeWhere,requestKey}: any) {
        commit('setLoadingData',true)
        const insight_teams = state.insight_teams
        const response: Array<any> = [];
        const happy: Array<any> = [];
        const risk: Array<any> = [];
        const activeuser: Array<any> = [];
        const alluser: Array<any> = [];
        const activemonth: Array<any> = [];
        if (value.length > 0 && insight_teams.length > 0) {
          const active: Array<any> = [];
          const team = (state.team === 'all') ? state.teamHeight : state.team
          await firebase.firestore().collection('active_users_teams').where("date", ">=",activeWhere.start).where('team','==',team).where('company','==',rootState.authen.user.company).get().then((querySnapshot)=> {
            querySnapshot.forEach((doc)=> {
                active[doc.data().date] = doc.data();
            });
          });
          if(activeWhere.type && activeWhere.type === 'months' && state.team === 'all'){
            await firebase.firestore().collection('active_users').where("date", ">=",activeWhere.start).where('company','==',rootState.authen.user.company).get().then((querySnapshot)=> {
              
              querySnapshot.forEach((doc)=> {
                activemonth[doc.data().year+'-'+doc.data().month+'-01'] = doc.data();
              });
            });
          }
          for (let m = 0; m < value.length; m++) {
            const _tmp: { [key: string]: number } = {}
            _tmp['sumhappy'] = 0
            _tmp['sumrisk'] = 0
            _tmp['rows'] = 0
            _tmp['totalusers'] = 0;
            _tmp['totalactive'] = 0;
            for (let index = 0; index < insight_teams.length; index++) {
              const element = insight_teams[index];
              // if(activeWhere.type && activeWhere.type === 'weeks') {
              //   if (element.date >= value[m]['startdate'] && element.date < value[m]['enddate']) {
              //     _tmp['sumhappy'] += element.happy_percent
              //     _tmp['sumrisk'] += element.risk_percent
              //     _tmp['rows'] += 1
              //   }
              // }else{
                if (element.date >= value[m]['startdate'] && element.date <= value[m]['enddate']) {
                  _tmp['sumhappy'] += element.happy_percent
                  _tmp['sumrisk'] += element.risk_percent
                  _tmp['rows'] += 1
                }
              // }
            }
            if(activeWhere.type && activeWhere.type === 'months' && state.team === 'all') {
              for(const x in activemonth){
                if (x >= value[m]['startdate'] && x <= value[m]['enddate']) {
                  _tmp['totalactive'] = (_tmp['totalactive'] < activemonth[x].active_users) ? activemonth[x].active_users: _tmp['totalactive'];
                }
              }
              for(const x in active){
                if (x >= value[m]['startdate'] && x <= value[m]['enddate']) {
                  _tmp['totalusers'] = (_tmp['totalusers'] < active[x].usersTotal) ? active[x].usersTotal: _tmp['totalusers'];
                }
              }
              if(_tmp['totalactive']>_tmp['totalusers']){
                _tmp['totalusers'] = _tmp['totalactive'];
              }
              activeuser.push((_tmp['totalactive'] > 0)? Math.min(100, Math.ceil(Number(_tmp['totalactive']*100) / _tmp['totalusers'])): 0)
              // activeuser.push((_tmp['totalactive'] > 0) ? Math.ceil(Number(_tmp['totalactive']*100) / _tmp['totalusers']) : 0)
              alluser.push(_tmp['totalactive'])
            }else if(activeWhere.type && (activeWhere.type === 'weeks' || activeWhere.type === 'months' )) {
              let users = [];
              for(const x in active){
                if (x >= value[m]['startdate'] && x <= value[m]['enddate']) {
                  _tmp['totalusers'] = (_tmp['totalusers'] < active[x].usersTotal) ? active[x].usersTotal: _tmp['totalusers'];
                  users = _.union(users,active[x].users);
                }
                // if(activeWhere.type === 'weeks'){
                //   if (x > value[m]['startdate'] && x <= value[m]['enddate']) {
                //     _tmp['totalusers'] = (_tmp['totalusers'] < active[x].usersTotal) ? active[x].usersTotal: _tmp['totalusers'];
                //     users = _.union(users,active[x].users);
                //   }
                // }else{
                //   if (x >= value[m]['startdate'] && x <= value[m]['enddate']) {
                //     _tmp['totalusers'] = (_tmp['totalusers'] < active[x].usersTotal) ? active[x].usersTotal: _tmp['totalusers'];
                //     users = _.union(users,active[x].users);
                //   }
                // }
              }
              activeuser.push((users.length > 0) ? Math.min(100, Math.ceil(Number(users.length*100) / _tmp['totalusers'])): 0)
              // activeuser.push((users.length > 0) ? Math.ceil(Number(users.length*100) / _tmp['totalusers']) : 0)
              alluser.push(users.length)
            }else{
              if(active[value[m]['startdate']]){
                if(active[value[m]['startdate']].usersActive>0){
                  activeuser.push(Math.min(100, Math.ceil((active[value[m]['startdate']].usersActive*100)/active[value[m]['startdate']].usersTotal)))
                  // activeuser.push(Math.ceil((active[value[m]['startdate']].usersActive*100)/active[value[m]['startdate']].usersTotal))
                }else{
                  activeuser.push(0);
                }
                alluser.push(active[value[m]['startdate']].usersActive);
              }else{
                activeuser.push(0);
                alluser.push(0);
              }
            }
            
            happy.push((_tmp['rows'] > 0) ? Math.ceil(_tmp['sumhappy'] / _tmp['rows']) : 0)
            risk.push((_tmp['rows'] > 0) ? Math.ceil(_tmp['sumrisk'] / _tmp['rows']) : 0)
            response['happy'] = happy
            response['risk'] = risk
            response['activeuser']= activeuser
            response['alluser']= alluser
          }
        }
        if (!isLatestScopedRequest(state, 'mainChart', requestKey)) {
          return response
        }

        commit('setLoadingData',false)
        commit('setChartHappyRisk', response)
        return response
      },
      // Fetch weekly active users for last 13 weeks for the selected team without touching main charts
      async getOverviewWeeklyActive({ commit, state, rootState }: any, { selectTeam, requestKey }: any) {
        try {
          const activeuser: Array<any> = []
          const alluser: Array<any> = []
          const dateNow = new Date()
          // Build last 13 weeks ranges (Mon-Sun, matching component logic)
          const startOfThisWeek = format(addDays(startOfWeek(dateNow), 1), 'YYYY-MM-DD')
          const endOfThisWeek = format(addDays(lastDayOfWeek(dateNow), 1), 'YYYY-MM-DD')
          const ranges: Array<{ startdate: string, enddate: string }> = []
          for (let j = 13 - 1; j >= 0; j--) {
            const startdate = format(subWeeks(startOfThisWeek, j), 'YYYY-MM-DD')
            const enddate = format(subWeeks(endOfThisWeek, j), 'YYYY-MM-DD')
            ranges.push({ startdate, enddate })
          }

          if (ranges.length === 0) {
            if (isLatestScopedRequest(state, 'main', requestKey)) {
              commit('setOverviewWeeklyActive', { activeuser, alluser })
            }
            return
          }

          // Earliest date to fetch
          const fetchStart = ranges[0].startdate
          const team = (!selectTeam || selectTeam === 'all') ? state.teamHeight : selectTeam
          if (!team) {
            if (isLatestScopedRequest(state, 'main', requestKey)) {
              commit('setOverviewWeeklyActive', { activeuser, alluser })
            }
            return
          }

          const active: { [date: string]: any } = {}
          await firebase.firestore().collection('active_users_teams')
            .where('date', '>=', fetchStart)
            .where('team', '==', team)
            .where('company', '==', rootState.authen.user.company)
            .get()
            .then((querySnapshot) => {
              querySnapshot.forEach((doc) => {
                active[doc.data().date] = doc.data()
              })
            })

          for (let m = 0; m < ranges.length; m++) {
            const range = ranges[m]
            let users: Array<any> = []
            let totalusers = 0
            for (const x in active) {
              if (x >= range.startdate && x <= range.enddate) {
                totalusers = (totalusers < active[x].usersTotal) ? active[x].usersTotal : totalusers
                users = _.union(users, active[x].users)
              }
            }
            const percent = (users.length > 0 && totalusers > 0) ? Math.min(100, Math.ceil((users.length * 100) / totalusers)) : 0
            activeuser.push(percent)
            alluser.push(users.length)
          }

          if (isLatestScopedRequest(state, 'main', requestKey)) {
            commit('setOverviewWeeklyActive', { activeuser, alluser })
          }
        } catch (e) {
          if (isLatestScopedRequest(state, 'main', requestKey)) {
            commit('setOverviewWeeklyActive', { activeuser: [], alluser: [] })
          }
        }
      },
      // Compute weekly happiness and risk for last 13 weeks from existing insight_teams
      async getOverviewWeeklyHealth({ commit, state }: any, { selectTeam, requestKey }: any) {
        try {
          const happy: Array<number> = []
          const risk: Array<number> = []
          const dateNow = new Date()
          const startOfThisWeek = format(addDays(startOfWeek(dateNow), 1), 'YYYY-MM-DD')
          const endOfThisWeek = format(addDays(lastDayOfWeek(dateNow), 1), 'YYYY-MM-DD')
          const ranges: Array<{ startdate: string, enddate: string }> = []
          for (let j = 13 - 1; j >= 0; j--) {
            const startdate = format(subWeeks(startOfThisWeek, j), 'YYYY-MM-DD')
            const enddate = format(subWeeks(endOfThisWeek, j), 'YYYY-MM-DD')
            ranges.push({ startdate, enddate })
          }

          const data = state.insight_teams || []
          if (!data || data.length === 0) {
            if (isLatestScopedRequest(state, 'main', requestKey)) {
              commit('setOverviewWeeklyHealth', { happy, risk })
            }
            return
          }

          for (let i = 0; i < ranges.length; i++) {
            const r = ranges[i]
            let sumHappy = 0
            let sumRisk = 0
            let rows = 0
            for (let k = 0; k < data.length; k++) {
              const element = data[k]
              if (element.team && selectTeam && element.team !== selectTeam) continue
              if (element.date >= r.startdate && element.date < r.enddate) {
                if (typeof element.happy_percent === 'number') {
                  sumHappy += element.happy_percent
                }
                if (typeof element.risk_percent === 'number') {
                  sumRisk += element.risk_percent
                }
                rows += 1
              }
            }
            happy.push(rows > 0 ? Math.ceil(sumHappy / rows) : 0)
            risk.push(rows > 0 ? Math.ceil(sumRisk / rows) : 0)
          }

          if (isLatestScopedRequest(state, 'main', requestKey)) {
            commit('setOverviewWeeklyHealth', { happy, risk })
          }
        } catch (e) {
          if (isLatestScopedRequest(state, 'main', requestKey)) {
            commit('setOverviewWeeklyHealth', { happy: [], risk: [] })
          }
        }
      },
      async getDataChartModal({ commit, state, rootState }: any, {value,activeWhere,requestKey}: any) {
        commit('setLoadingDataModal',true)
        const insight_teams = state.insight_teams_modal
        const questions = state.question_category
        const response: Array<any> = [];
        const happy: Array<any> = [];
        const risk: Array<any> = [];
        const activeuser: Array<any> = [];
        const alluser: Array<any> = [];
        const activemonth: Array<any> = [];
        const satisfaction: Array<any> = [];
        const feedback: Array<any> = [];
        const wellness: Array<any> = [];
        const manager: Array<any> = [];
        const colleagues: Array<any> = [];
        const ambassadorship: Array<any> = [];
        const personal: Array<any> = [];
        const recognition: Array<any> = [];
        const alignment: Array<any> = [];
        if (value.length > 0 && insight_teams.length > 0) {
          const active: Array<any> = [];
          const team = (state.teammodal === 'all') ? state.teamHeightModal : state.teammodal
          await firebase.firestore().collection('active_users_teams').where("date", ">=",activeWhere.start).where('team','==',team).where('company','==',rootState.authen.user.company).get().then((querySnapshot)=> {
            querySnapshot.forEach((doc)=> {
                active[doc.data().date] = doc.data();
            });
          });
          
          if(activeWhere.type && activeWhere.type === 'months' && state.teammodal === 'all'){
            await firebase.firestore().collection('active_users').where("date", ">=",activeWhere.start).where('company','==',rootState.authen.user.company).get().then((querySnapshot)=> {
              querySnapshot.forEach((doc)=> {
                activemonth[doc.data().year+'-'+doc.data().month+'-01'] = doc.data();
              });
            });
          }
          const insightTeamsLineChart: Array<any> = [];
          await firebase.firestore().collection('insight_teams').where("team","==",team).where("date", ">=", activeWhere.start).orderBy('date', 'asc').get()
          .then(function (querySnapshot) {
              querySnapshot.forEach(function (doc) {
                const _data = doc.data()
                _data.category = questions[_data.quest_id]
                insightTeamsLineChart.push(_data)
              });
          });
          for (let m = 0; m < value.length; m++) {
            const element = insightTeamsLineChart[m];
            const _tmp2: { [key: string]: { score: number, color: string } } = {}
            _tmp2['satisfaction'] = { score: 0, color: '#C7C7C7' }
            _tmp2['feedback'] = { score: 0, color: '#C7C7C7' }
            _tmp2['wellness'] = { score: 0, color: '#C7C7C7' }
            _tmp2['manager'] = { score: 0, color: '#C7C7C7' }
            _tmp2['colleagues'] = { score: 0, color: '#C7C7C7' }
            _tmp2['ambassadorship'] = { score: 0, color: '#C7C7C7' }
            _tmp2['personal'] = { score: 0, color: '#C7C7C7' }
            _tmp2['recognition'] = { score: 0, color: '#C7C7C7' }
            _tmp2['alignment'] = { score: 0, color: '#C7C7C7' }
            if(activeWhere.type && (activeWhere.type === 'weeks')) {
              for (let index = 0; index < insightTeamsLineChart.length; index++) {
                const element2 = insightTeamsLineChart[index];
                if (element2.date >= value[m]['startdate'] && element2.date <= value[m]['enddate']) {
                  if (element2.category === '-KxRzHeGK7cuwKtAKQwF'){
                    _tmp2['satisfaction'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxS-JNn6sNeqTD0KE53'){
                    _tmp2['feedback'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxS05m9KaS70Ai6ps93') {
                    _tmp2['wellness'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxS-e4O2--BrGiRWbEF') {
                    _tmp2['manager'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxRyiIXeDebHreB12mM') {
                    _tmp2['colleagues'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxRzbMWTgeqpsSbCo_6') {
                    _tmp2['ambassadorship'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxRdFigA6geGzv2zJHQ') {
                    _tmp2['personal'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxRwgLlN0DpB0p7al7N') {
                    _tmp2['recognition'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxS15s4DhoKSrjs4D7k') {
                    _tmp2['alignment'] = { score: element2.percent, color: state.optColor[element2.color] }
                  }
                }
              }
            }else if(activeWhere.type && activeWhere.type === 'months'){
              for (let index = 0; index < insightTeamsLineChart.length; index++) {
                const element2 = insightTeamsLineChart[index];
                if (element2.date >= value[m]['startdate'] && element2.date <= value[m]['enddate']) {
                  if (element2.category === '-KxRzHeGK7cuwKtAKQwF'){
                    _tmp2['satisfaction'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxS-JNn6sNeqTD0KE53'){
                    _tmp2['feedback'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxS05m9KaS70Ai6ps93') {
                    _tmp2['wellness'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxS-e4O2--BrGiRWbEF') {
                    _tmp2['manager'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxRyiIXeDebHreB12mM') {
                    _tmp2['colleagues'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxRzbMWTgeqpsSbCo_6') {
                    _tmp2['ambassadorship'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxRdFigA6geGzv2zJHQ') {
                    _tmp2['personal'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxRwgLlN0DpB0p7al7N') {
                    _tmp2['recognition'] = { score: element2.percent, color: state.optColor[element2.color] }
                  } else if (element2.category === '-KxS15s4DhoKSrjs4D7k') {
                    _tmp2['alignment'] = { score: element2.percent, color: state.optColor[element2.color] }
                  }
                }
              }
            }else{
              if (element.quest_id === '-Kgs61AHOnpJ3rfyQr6t') {
                if(satisfaction[satisfaction.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L1FmpFO-v8IZc_B5EB7').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    satisfaction.push(datacheck)
                  });
                }else{
                  satisfaction.push(satisfaction[satisfaction.length - 1]);
                }
                if(feedback[feedback.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L1QWPtZvax_KlJgpNxF').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    feedback.push(datacheck)
                  });
                }else{
                  feedback.push(feedback[feedback.length - 1]);
                }
                if(wellness[wellness.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L36rxTgLhZTL4mq4SV1').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    wellness.push(datacheck)
                  });
                }else{
                  wellness.push(wellness[wellness.length - 1]);
                }
                if(manager[manager.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L1Qug-PhtzVOR2Dr5q7').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    manager.push(datacheck)
                  });
                }else{
                  manager.push(manager[manager.length - 1]);
                }
                if(colleagues[colleagues.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-Kgs549ClADmNQwoNbbG').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    colleagues.push(datacheck)
                  });
                }else{
                  colleagues.push(colleagues[colleagues.length - 1]);
                }
                if(ambassadorship[ambassadorship.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L2ycGiBDo3rDGjLNKwW').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    ambassadorship.push(datacheck)
                  });
                }else{
                  ambassadorship.push(ambassadorship[ambassadorship.length - 1]);
                }
                if(personal[personal.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-Kgn7oxLPPWlbM_ZDc_h').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    personal.push(datacheck)
                  });
                }else{
                  personal.push(personal[personal.length - 1]);
                }
                if(recognition[recognition.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L0J5FFsGQYVkfcGujWo').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    recognition.push(datacheck)
                  });
                }else{
                  recognition.push(recognition[recognition.length - 1]);
                }
                if(alignment[alignment.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-LtOL_iR1N-ev2b_8Tmg').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    alignment.push(datacheck)
                  });
                }else{
                  alignment.push(alignment[alignment.length - 1]);
                }
              } else {
                if (element.category === '-KxRzHeGK7cuwKtAKQwF'){
                    satisfaction.push(Math.ceil(element.percent))
                }else{
                  if(satisfaction[satisfaction.length - 1] == undefined){
                    let datacheck = 0
                    await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L1FmpFO-v8IZc_B5EB7').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                    .then(function (querySnapshot) {
                      querySnapshot.forEach(function (doc) {
                        const _data = doc.data()
                        datacheck = Math.ceil(_data.percent)
                      });
                      satisfaction.push(datacheck)
                    });
                  }else{
                    satisfaction.push(satisfaction[satisfaction.length - 1]);
                  }
                }
                if (element.category === '-KxS-JNn6sNeqTD0KE53'){
                    feedback.push(Math.ceil(element.percent))
                }else{
                  if(feedback[feedback.length - 1] == undefined){
                    let datacheck = 0
                    await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L1QWPtZvax_KlJgpNxF').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                    .then(function (querySnapshot) {
                      querySnapshot.forEach(function (doc) {
                        const _data = doc.data()
                        datacheck = Math.ceil(_data.percent)
                      });
                      feedback.push(datacheck)
                    });
                  }else{
                    feedback.push(feedback[feedback.length - 1]);
                  }
                }
                if (element.category === '-KxS05m9KaS70Ai6ps93'){
                  wellness.push(Math.ceil(element.percent))
                }else{
                  if(wellness[wellness.length - 1] == undefined){
                    let datacheck = 0
                    await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L36rxTgLhZTL4mq4SV1').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                    .then(function (querySnapshot) {
                      querySnapshot.forEach(function (doc) {
                        const _data = doc.data()
                        datacheck = Math.ceil(_data.percent)
                      });
                      wellness.push(datacheck)
                    });
                  }else{
                    wellness.push(wellness[wellness.length - 1]);
                  }
                }
                if (element.category === '-KxS-e4O2--BrGiRWbEF'){
                  manager.push(Math.ceil(element.percent))
                }else{
                  if(manager[manager.length - 1] == undefined){
                    let datacheck = 0
                    await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L1Qug-PhtzVOR2Dr5q7').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                    .then(function (querySnapshot) {
                      querySnapshot.forEach(function (doc) {
                        const _data = doc.data()
                        datacheck = Math.ceil(_data.percent)
                      });
                      manager.push(datacheck)
                    });
                  }else{
                    manager.push(manager[manager.length - 1]);
                  }
                }
                if (element.category === '-KxRyiIXeDebHreB12mM'){
                  colleagues.push(Math.ceil(element.percent))
                }else{
                  if(colleagues[colleagues.length - 1] == undefined){
                    let datacheck = 0
                    await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-Kgs549ClADmNQwoNbbG').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                    .then(function (querySnapshot) {
                      querySnapshot.forEach(function (doc) {
                        const _data = doc.data()
                        datacheck = Math.ceil(_data.percent)
                      });
                      colleagues.push(datacheck)
                    });
                  }else{
                    colleagues.push(colleagues[colleagues.length - 1]);
                  }
                }
                if (element.category === '-KxRzbMWTgeqpsSbCo_6'){
                  ambassadorship.push(Math.ceil(element.percent))
                }else{
                  if(ambassadorship[ambassadorship.length - 1] == undefined){
                    let datacheck = 0
                    await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L2ycGiBDo3rDGjLNKwW').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                    .then(function (querySnapshot) {
                      querySnapshot.forEach(function (doc) {
                        const _data = doc.data()
                        datacheck = Math.ceil(_data.percent)
                      });
                      ambassadorship.push(datacheck)
                    });
                  }else{
                    ambassadorship.push(ambassadorship[ambassadorship.length - 1]);
                  }
                }
                if (element.category === '-KxRdFigA6geGzv2zJHQ'){
                  personal.push(Math.ceil(element.percent))
                }else{
                  if(personal[personal.length - 1] == undefined){
                    let datacheck = 0
                    await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-Kgn7oxLPPWlbM_ZDc_h').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                    .then(function (querySnapshot) {
                      querySnapshot.forEach(function (doc) {
                        const _data = doc.data()
                        datacheck = Math.ceil(_data.percent)
                      });
                      personal.push(datacheck)
                    });
                  }else{
                    personal.push(personal[personal.length - 1]);
                  }
                }
                if (element.category === '-KxRwgLlN0DpB0p7al7N'){
                  recognition.push(Math.ceil(element.percent))
                }else{
                  if(recognition[recognition.length - 1] == undefined){
                    let datacheck = 0
                    await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L0J5FFsGQYVkfcGujWo').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                    .then(function (querySnapshot) {
                      querySnapshot.forEach(function (doc) {
                        const _data = doc.data()
                        datacheck = Math.ceil(_data.percent)
                      });
                      recognition.push(datacheck)
                    });
                  }else{
                    recognition.push(recognition[recognition.length - 1]);
                  }
                }
                if (element.category === '-KxS15s4DhoKSrjs4D7k'){
                  alignment.push(Math.ceil(element.percent))
                }else{
                  if(alignment[alignment.length - 1] == undefined){
                    let datacheck = 0
                    await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-LtOL_iR1N-ev2b_8Tmg').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                    .then(function (querySnapshot) {
                      querySnapshot.forEach(function (doc) {
                        const _data = doc.data()
                        datacheck = Math.ceil(_data.percent)
                      });
                      alignment.push(datacheck)
                    });
                  }else{
                    alignment.push(alignment[alignment.length - 1]);
                  }
                }
              }
            }
            const _tmp: { [key: string]: number } = {}
            _tmp['sumhappy'] = 0
            _tmp['sumrisk'] = 0
            _tmp['rows'] = 0
            _tmp['totalusers'] = 0;
            _tmp['totalactive'] = 0;
            for (let index = 0; index < insight_teams.length; index++) {
              const element = insight_teams[index];
              // if(activeWhere.type && activeWhere.type === 'weeks') {
              //   if (element.date >= value[m]['startdate'] && element.date < value[m]['enddate']) {
              //     _tmp['sumhappy'] += element.happy_percent
              //     _tmp['sumrisk'] += element.risk_percent
              //     _tmp['rows'] += 1
              //   }
              // }else{
                if (element.date >= value[m]['startdate'] && element.date <= value[m]['enddate']) {
                  _tmp['sumhappy'] += element.happy_percent
                  _tmp['sumrisk'] += element.risk_percent
                  _tmp['rows'] += 1
                }
              // }
            }
            if(activeWhere.type && activeWhere.type === 'months' && state.teammodal === 'all') {
              for(const x in activemonth){
                if (x >= value[m]['startdate'] && x <= value[m]['enddate']) {
                  _tmp['totalactive'] = (_tmp['totalactive'] < activemonth[x].active_users) ? activemonth[x].active_users: _tmp['totalactive'];
                }
              }
              for(const x in active){
                if (x >= value[m]['startdate'] && x <= value[m]['enddate']) {
                  _tmp['totalusers'] = (_tmp['totalusers'] < active[x].usersTotal) ? active[x].usersTotal: _tmp['totalusers'];
                }
              }
              activeuser.push((_tmp['totalactive'] > 0) ? Math.min(100, Math.ceil(Number(_tmp['totalactive']*100) / _tmp['totalusers'])) : 0 )
              // activeuser.push((_tmp['totalactive'] > 0) ? Math.ceil(Number(_tmp['totalactive']*100) / _tmp['totalusers']) : 0)
              alluser.push(_tmp['totalactive'])
            }else if(activeWhere.type && (activeWhere.type === 'weeks' || activeWhere.type === 'months' )) {
              let users = [];
              for(const x in active){
                // if(activeWhere.type === 'weeks'){
                //   if (x > value[m]['startdate'] && x <= value[m]['enddate']) {
                //     _tmp['totalusers'] = (_tmp['totalusers'] < active[x].usersTotal) ? active[x].usersTotal: _tmp['totalusers'];
                //     users = _.union(users,active[x].users);
                //   }
                // }else{
                  if (x >= value[m]['startdate'] && x <= value[m]['enddate']) {
                    _tmp['totalusers'] = (_tmp['totalusers'] < active[x].usersTotal) ? active[x].usersTotal: _tmp['totalusers'];
                    users = _.union(users,active[x].users);
                  }
                // }
              }
              activeuser.push((users.length > 0) ? Math.min(100, Math.ceil(Number(users.length*100) / _tmp['totalusers'])) : 0)
              // activeuser.push((users.length > 0) ? Math.ceil(Number(users.length*100) / _tmp['totalusers']) : 0)
              alluser.push(users.length)
            }else{
              if(active[value[m]['startdate']]){
                if(active[value[m]['startdate']].usersActive>0){
                  activeuser.push(Math.min(100, Math.ceil((active[value[m]['startdate']].usersActive*100)/active[value[m]['startdate']].usersTotal)))
                  // activeuser.push(Math.ceil((active[value[m]['startdate']].usersActive*100)/active[value[m]['startdate']].usersTotal))
                }else{
                  activeuser.push(0);
                }
                alluser.push(active[value[m]['startdate']].usersActive);
              }else{
                activeuser.push(0);
                alluser.push(0);
              }
            }
            
            happy.push((_tmp['rows'] > 0) ? Math.ceil(_tmp['sumhappy'] / _tmp['rows']) : 0)
            risk.push((_tmp['rows'] > 0) ? Math.ceil(_tmp['sumrisk'] / _tmp['rows']) : 0)
            response['happy'] = happy
            response['risk'] = risk
            response['activeuser']= activeuser
            response['alluser']= alluser

            if(activeWhere.type && (activeWhere.type === 'months' || activeWhere.type === 'weeks')) {
              if(_tmp2['satisfaction'].score > 0){
                satisfaction.push(_tmp2['satisfaction'])
              }else{
                if(satisfaction[satisfaction.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L1FmpFO-v8IZc_B5EB7').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    satisfaction.push({ score: datacheck, color: '#C7C7C7' })
                  });
                }else{
                  satisfaction.push(satisfaction[satisfaction.length - 1]);
                }
              }
              if(_tmp2['feedback'].score > 0){
                feedback.push(_tmp2['feedback'])
              }else{
                if(feedback[feedback.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L1QWPtZvax_KlJgpNxF').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    feedback.push({ score: datacheck, color: '#C7C7C7' })
                  });
                }else{
                  feedback.push(feedback[feedback.length - 1]);
                }
              }
              if(_tmp2['wellness'].score > 0){
                wellness.push(_tmp2['wellness'])
              }else{
                if(wellness[wellness.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L36rxTgLhZTL4mq4SV1').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    wellness.push({ score: datacheck, color: '#C7C7C7' })
                  });
                }else{
                  wellness.push(wellness[wellness.length - 1]);
                }
              }
              if(_tmp2['manager'].score > 0){
                manager.push(_tmp2['manager'])
              }else{
                if(manager[manager.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L1Qug-PhtzVOR2Dr5q7').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    manager.push({ score: datacheck, color: '#C7C7C7' })
                  });
                }else{
                  manager.push(manager[manager.length - 1]);
                }
              }
              if(_tmp2['colleagues'].score > 0){
                colleagues.push(_tmp2['colleagues'])
              }else{
                if(colleagues[colleagues.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-Kgs549ClADmNQwoNbbG').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    colleagues.push({ score: datacheck, color: '#C7C7C7' })
                  });
                }else{
                  colleagues.push(colleagues[colleagues.length - 1]);
                }
              }
              if(_tmp2['ambassadorship'].score > 0){
                ambassadorship.push(_tmp2['ambassadorship'])
              }else{
                if(ambassadorship[ambassadorship.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L2ycGiBDo3rDGjLNKwW').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    ambassadorship.push({ score: datacheck, color: '#C7C7C7' })
                  });
                }else{
                  ambassadorship.push(ambassadorship[ambassadorship.length - 1]);
                }
              }
              if(_tmp2['personal'].score > 0){
                personal.push(_tmp2['personal'])
              }else{
                if(personal[personal.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-Kgn7oxLPPWlbM_ZDc_h').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    personal.push({ score: datacheck, color: '#C7C7C7' })
                  });
                }else{
                  personal.push(personal[personal.length - 1]);
                }
              }
              if(_tmp2['recognition'].score > 0){
                recognition.push(_tmp2['recognition'])
              }else{
                if(recognition[recognition.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-L0J5FFsGQYVkfcGujWo').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    recognition.push({ score: datacheck, color: '#C7C7C7' })
                  });
                }else{
                  recognition.push(recognition[recognition.length - 1]);
                }
              }
              if(_tmp2['alignment'].score > 0){
                alignment.push(_tmp2['alignment'])
              }else{
                if(alignment[alignment.length - 1] == undefined){
                  let datacheck = 0
                  await firebase.firestore().collection('insight_teams').where("team","==",team).where("quest_id","==",'-LtOL_iR1N-ev2b_8Tmg').where("date", "<=", element.date).orderBy('date', 'desc').limit(1).get()
                  .then(function (querySnapshot) {
                    querySnapshot.forEach(function (doc) {
                      const _data = doc.data()
                      datacheck = Math.ceil(_data.percent)
                    });
                    alignment.push({ score: datacheck, color: '#C7C7C7' })
                  });
                }else{
                  alignment.push(alignment[alignment.length - 1]);
                }
              }
            }

            response['satisfaction'] = satisfaction
            response['feedback'] = feedback
            response['wellness'] = wellness
            response['manager'] = manager
            response['colleagues'] = colleagues
            response['ambassadorship'] = ambassadorship
            response['personal'] = personal
            response['recognition'] = recognition
            response['alignment'] = alignment
          }
        }
        if (!isLatestScopedRequest(state, 'modalChart', requestKey)) {
          return response
        }

        commit('setLoadingDataModal',false)
        commit('setChartHappyRiskModal', response)
        return response
      },
      async getDataTable({ commit, dispatch, state, rootState }: any, payload:any) {
        const team = payload && typeof payload === 'object'
          ? payload.team
          : payload
        const requestKey = payload && typeof payload === 'object'
          ? payload.requestKey
          : undefined
        const skipTreeDispatch = !!(payload && typeof payload === 'object' && payload.skipTreeDispatch)
        // ตรวจสอบว่า impactRating พร้อมหรือยัง
        if (Object.keys(state.impactRating).length === 0) {
          // โหลด impactRating ก่อน
          await dispatch('getTeamOrganization1level', rootState.authen.user);
        }
        const insightteamsByComp = state.insightteamsByComp
        // const optColor = { 'yellow': '#FBC02D', 'green': '#66BB6A', 'red': '#F44336'}
        const teams: Array<any> = [];
        const engagementTeam: { [key: string]: any } = {};
        const axios = require('axios');
        const company = rootState.authen.user.company
        const teamData = await getTeamByCompany(company)
        for(const key in teamData){
          const team = teamData[key]
          team.key = key
          if(rootState.authen.user.user_type == 'leadership'){
            if(state.organization1level[team.key]){
              teams.push(team)
            }
          }else{
            teams.push(team)
          }

        }

        const engagementDate = format(addDays(new Date(), -1), 'YYYY-MM-DD')
        const cachedEngagementTeam = readTeamsViewCache(company, `engagementTeam:${engagementDate}`)
        if (cachedEngagementTeam && Object.keys(cachedEngagementTeam).length > 0) {
          Object.keys(cachedEngagementTeam).forEach((key) => {
            engagementTeam[key] = cachedEngagementTeam[key]
          })
        } else {
          await firebase.firestore().collection('engagement_index_30day_rolling_team').where("company", "==", company).where("date", "==", engagementDate).get()
            .then((querySnapshot) => {
              querySnapshot.forEach((doc) => {
                const _data = doc.data()
                engagementTeam[_data.team] = _data
              });
              writeTeamsViewCache(company, `engagementTeam:${engagementDate}`, engagementTeam)
            });
        }
        if (teams.length > 0 && insightteamsByComp.length > 0) {
          const response: Array<any> = [];
          const data: { [key: string]: {} } = {};
          for (let i = 0; i < teams.length; i++) {
            const _tmp: { [key: string]: number } = {}
            _tmp['happy_percent'] = 0
            _tmp['risk_percent'] = 0
            const _tmp2: { [key: string]: { score: number, color: string } } = {}
            _tmp2['satisfaction'] = { score: 0, color: '#C7C7C7' }
            _tmp2['feedback'] = { score: 0, color: '#C7C7C7' }
            _tmp2['wellness'] = { score: 0, color: '#C7C7C7' }
            _tmp2['manager'] = { score: 0, color: '#C7C7C7' }
            _tmp2['colleagues'] = { score: 0, color: '#C7C7C7' }
            _tmp2['ambassadorship'] = { score: 0, color: '#C7C7C7' }
            _tmp2['personal'] = { score: 0, color: '#C7C7C7' }
            _tmp2['recognition'] = { score: 0, color: '#C7C7C7' }
            _tmp2['alignment'] = { score: 0, color: '#C7C7C7' }
            for (let index = 0; index < insightteamsByComp.length; index++) {
              const element = insightteamsByComp[index];
              if (element.team === teams[i].key){
                if (element.quest_id === '-Kgs61AHOnpJ3rfyQr6t') {
                  _tmp['happirate'] = element.happy_percent
                  _tmp['riskrate'] = element.risk_percent
                } else {
                  if (element.category === '-KxRzHeGK7cuwKtAKQwF'){
                    _tmp2['satisfaction'] = { score: element.percent, color: state.optColor[element.color] }
                  } else if (element.category === '-KxS-JNn6sNeqTD0KE53'){
                    _tmp2['feedback'] = { score: element.percent, color: state.optColor[element.color] }
                  } else if (element.category === '-KxS05m9KaS70Ai6ps93') {
                    _tmp2['wellness'] = { score: element.percent, color: state.optColor[element.color] }
                  } else if (element.category === '-KxS-e4O2--BrGiRWbEF') {
                    _tmp2['manager'] = { score: element.percent, color: state.optColor[element.color] }
                  } else if (element.category === '-KxRyiIXeDebHreB12mM') {
                    _tmp2['colleagues'] = { score: element.percent, color: state.optColor[element.color] }
                  } else if (element.category === '-KxRzbMWTgeqpsSbCo_6') {
                    _tmp2['ambassadorship'] = { score: element.percent, color: state.optColor[element.color] }
                  } else if (element.category === '-KxRdFigA6geGzv2zJHQ') {
                    _tmp2['personal'] = { score: element.percent, color: state.optColor[element.color] }
                  } else if (element.category === '-KxRwgLlN0DpB0p7al7N') {
                    _tmp2['recognition'] = { score: element.percent, color: state.optColor[element.color] }
                                          } else if (element.category === '-KxS15s4DhoKSrjs4D7k') {
                     _tmp2['alignment'] = { score: element.percent, color: state.optColor[element.color] }
                  }
                }
                _tmp2['total'] = element.total
              }
            }

            data[teams[i].key] = {
              id: teams[i].key,
              team: teams[i].name,
              happirate: (Math.ceil(_tmp['happirate']))? Math.ceil(_tmp['happirate']) : 0,
              riskrate: (Math.ceil(_tmp['riskrate'])) ? Math.ceil(_tmp['riskrate']) : 0,
              satisfaction: _tmp2['satisfaction'],
              feedback: _tmp2['feedback'],
              wellness: _tmp2['wellness'],
              manager: _tmp2['manager'],
              colleagues: _tmp2['colleagues'],
              ambassadorship: _tmp2['ambassadorship'],
              personal: _tmp2['personal'],
              recognition: _tmp2['recognition'],
              alignment: _tmp2['alignment'],
              total: _tmp2['total'],
              avgimpact:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].Avgimpact:'-',
              avgimpact_color:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].avgimpact_color:"red",
              retention_rate:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].Retention_rate:'-',
              retention_color:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].color:"red",
              terminate:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].terminated:0,
              resigned:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].resigned:0,
              other:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].other:0,
              engagement_score:engagementTeam[teams[i].key]?engagementTeam[teams[i].key].team_DEBI:'-',
            }
            response.push(data[teams[i].key])
          }
          const sortedResponse = _.sortBy(response, [function (o: any) { return o.team; }])
          commit('setDataTableAll', sortedResponse)
          if (!skipTreeDispatch) {
            return dispatch('getDataTablebyTeam', {
              team: (team == 'all') ? '' : team,
              type: 'parent',
              requestKey,
              teamData
            })
          }
          return sortedResponse
        }
        commit('setDataTableAll', [])
        return []
        // await firebase.database().ref('team').orderByChild('company').equalTo(`${rootState.authen.user.company}`).once('value', snapshot => {
        //   snapshot.forEach((doc): any => {
        //     const team = doc.val()
        //     team.key = doc.key
        //     if(rootState.authen.user.user_type == 'leadership'){
        //       if(state.organization1level[team.key]){
        //         teams.push(team)
        //       }
        //     }else{
        //       teams.push(team)
        //     }
        //   });
        //   firebase.firestore().collection('engagement_index_30day_rolling_team').where("company", "==", rootState.authen.user.company).where("date", "==", format(addDays(new Date(), -1), 'YYYY-MM-DD')).get()
        //     .then((querySnapshot) => {
        //       querySnapshot.forEach((doc) => {
        //         const _data = doc.data()
        //         engagementTeam[_data.team] = _data
        //       });
        //       if (teams.length > 0 && insightteamsByComp.length > 0) {
        //         const response: Array<any> = [];
        //         const data: { [key: string]: {} } = {};
        //         for (let i = 0; i < teams.length; i++) {
        //           const _tmp: { [key: string]: number } = {}
        //           _tmp['happy_percent'] = 0
        //           _tmp['risk_percent'] = 0
        //           const _tmp2: { [key: string]: { score: number, color: string } } = {}
        //           _tmp2['satisfaction'] = { score: 0, color: '#C7C7C7' }
        //           _tmp2['feedback'] = { score: 0, color: '#C7C7C7' }
        //           _tmp2['wellness'] = { score: 0, color: '#C7C7C7' }
        //           _tmp2['manager'] = { score: 0, color: '#C7C7C7' }
        //           _tmp2['colleagues'] = { score: 0, color: '#C7C7C7' }
        //           _tmp2['ambassadorship'] = { score: 0, color: '#C7C7C7' }
        //           _tmp2['personal'] = { score: 0, color: '#C7C7C7' }
        //           _tmp2['recognition'] = { score: 0, color: '#C7C7C7' }
        //           _tmp2['alignment'] = { score: 0, color: '#C7C7C7' }
        //           for (let index = 0; index < insightteamsByComp.length; index++) {
        //             const element = insightteamsByComp[index];
        //             if (element.team === teams[i].key){
        //               if (element.quest_id === '-Kgs61AHOnpJ3rfyQr6t') {
        //                 _tmp['happirate'] = element.happy_percent
        //                 _tmp['riskrate'] = element.risk_percent
        //               } else {
        //                 if (element.category === '-KxRzHeGK7cuwKtAKQwF'){
        //                   _tmp2['satisfaction'] = { score: element.percent, color: state.optColor[element.color] }
        //                 } else if (element.category === '-KxS-JNn6sNeqTD0KE53'){
        //                   _tmp2['feedback'] = { score: element.percent, color: state.optColor[element.color] }
        //                 } else if (element.category === '-KxS05m9KaS70Ai6ps93') {
        //                   _tmp2['wellness'] = { score: element.percent, color: state.optColor[element.color] }
        //                 } else if (element.category === '-KxS-e4O2--BrGiRWbEF') {
        //                   _tmp2['manager'] = { score: element.percent, color: state.optColor[element.color] }
        //                 } else if (element.category === '-KxRyiIXeDebHreB12mM') {
        //                   _tmp2['colleagues'] = { score: element.percent, color: state.optColor[element.color] }
        //                 } else if (element.category === '-KxRzbMWTgeqpsSbCo_6') {
        //                   _tmp2['ambassadorship'] = { score: element.percent, color: state.optColor[element.color] }
        //                 } else if (element.category === '-KxRdFigA6geGzv2zJHQ') {
        //                   _tmp2['personal'] = { score: element.percent, color: state.optColor[element.color] }
        //                 } else if (element.category === '-KxRwgLlN0DpB0p7al7N') {
        //                   _tmp2['recognition'] = { score: element.percent, color: state.optColor[element.color] }
        //                                           } else if (element.category === '-KxS15s4DhoKSrjs4D7k') {
        //                    _tmp2['alignment'] = { score: element.percent, color: state.optColor[element.color] }
        //                 }
        //               }
        //               _tmp2['total'] = element.total
        //             }
        //           }
      
        //           data[teams[i].key] = {
        //             id: teams[i].key,
        //             team: teams[i].name,
        //             happirate: (Math.ceil(_tmp['happirate']))? Math.ceil(_tmp['happirate']) : 0,
        //             riskrate: (Math.ceil(_tmp['riskrate'])) ? Math.ceil(_tmp['riskrate']) : 0,
        //             satisfaction: _tmp2['satisfaction'],
        //             feedback: _tmp2['feedback'],
        //             wellness: _tmp2['wellness'],
        //             manager: _tmp2['manager'],
        //             colleagues: _tmp2['colleagues'],
        //             ambassadorship: _tmp2['ambassadorship'],
        //             personal: _tmp2['personal'],
        //             recognition: _tmp2['recognition'],
        //             alignment: _tmp2['alignment'],
        //             total: _tmp2['total'],
        //             avgimpact:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].Avgimpact:'-',
        //             avgimpact_color:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].avgimpact_color:"red",
        //             retention_rate:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].Retention_rate:'-',
        //             retention_color:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].color:"red",
        //             terminate:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].terminated:0,
        //             resigned:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].resigned:0,
        //             other:state.impactRating[teams[i].key]?state.impactRating[teams[i].key].other:0,
        //             engagement_score:engagementTeam[teams[i].key]?engagementTeam[teams[i].key].team_DEBI:'-',
        //           }
        //           response.push(data[teams[i].key])
        //         } 
        //         commit('setDataTableAll', _.sortBy(response, [function (o: any) { return o.team; }]))
        //         dispatch('getDataTablebyTeam', { team: (team == 'all') ? '': team, type: 'parent' })
        //       }
        //     });
        // });
      },
      async getDataTablebyTeam({ commit, state, dispatch, rootState }: any, data: any) {
        const teamData = data.teamData || await getTeamByCompany(rootState.authen.user.company)

        if (!data.team) {
          data.team = getTopmostTeamId(teamData)
        }

        if (
          (!state.datalistall || state.datalistall.length === 0) &&
          state.insightteamsByComp &&
          state.insightteamsByComp.length > 0
        ) {
          await dispatch('getDataTable', {
            team: data.team || '',
            requestKey: data.requestKey,
            skipTreeDispatch: true
          })
        }

        if (!data.team) {
          if (data.type == 'parent' && isLatestScopedRequest(state, 'main', data.requestKey)) {
            commit('setDataTable', [])
          }
          return []
        }

        const response = await buildTeamTreeBranch({
          actionName: 'getDataTablebyTeam',
          dispatch,
          state,
          teamData,
          teamId: data.team,
          type: data.type,
          requestKey: data.requestKey
        })

        if (data.type == 'parent') {
          if (!isLatestScopedRequest(state, 'main', data.requestKey)) {
            return response
          }
          commit('setDataTable', response)
        }
        return response
      },
      async getDataTablebyTeamModal({ commit, state, dispatch, rootState }: any, data: any) {
        const teamData = data.teamData || await getTeamByCompany(rootState.authen.user.company)

        if (!data.team) {
          data.team = getTopmostTeamId(teamData)
        }

        if (
          (!state.datalistall || state.datalistall.length === 0) &&
          state.insightteamsByComp &&
          state.insightteamsByComp.length > 0
        ) {
          await dispatch('getDataTable', {
            team: data.team || '',
            requestKey: data.requestKey,
            skipTreeDispatch: true
          })
        }

        if (!data.team) {
          if (data.type == 'parent' && isLatestScopedRequest(state, 'modal', data.requestKey)) {
            commit('setDataTableModal', [])
          }
          return []
        }

        const response = await buildTeamTreeBranch({
          actionName: 'getDataTablebyTeamModal',
          dispatch,
          state,
          teamData,
          teamId: data.team,
          type: data.type,
          requestKey: data.requestKey
        })

        if (data.type == 'parent') {
          if (!isLatestScopedRequest(state, 'modal', data.requestKey)) {
            return response
          }
          commit('setDataTableModal', response)
        }
        return response
      },
      getDataTableSort({ commit, state }: any, sort: string) {
        if (sort == '1') {
          commit('setDataTable', _.orderBy(state.datalistall, [function (o: any) { return o.happirate; }], ['desc']))
        } else if (sort == '2') {
          commit('setDataTable', _.orderBy(state.datalistall, [function (o: any) { return o.riskrate; }], ['asc']))
        } else if (sort == '3') {
          commit('setDataTable', _.orderBy(state.datalistall, [function (o: any) { return o.riskrate; }], ['desc']))
        } else {
          commit('setDataTable', _.orderBy(state.datalistall, [function (o: any) { return o.happirate; }], ['asc']))
        }
      },
      getMostEngage({commit,state}:any, user:any){
        const dateNow = new Date()
        let today = format(dateNow, 'YYYY-WW')
        firebase.firestore().collection('spotlight').where("type", "==",'most_engaged').where("date", "==",today).where("company", "==", user.company).orderBy('score', 'asc').get()
        .then((querySnapshot)=> {
          let list_MostEngageFirstTime:Array<any> = []
          let list_MostEngage:Array<any> = []
          let list_MostEngage_root:Array<any> = []
          if(querySnapshot.empty){
            let today2 = format(addDays(dateNow, -6), 'YYYY-WW')
            firebase.firestore().collection('spotlight').where("type", "==",'most_engaged').where("date", "==",today2).where("company", "==", user.company).orderBy('score', 'asc').get()
              .then((querySnapshot)=> {
                querySnapshot.forEach( (doc)=> {
                  let temp_name = '';
                  let temp_name_parent = '';
                  if(state.teamArr[doc.data().team]){
                  temp_name = state.teamArr[doc.data().team].name
                  temp_name_parent = (state.teamArr[doc.data().team].parent != "" ? state.teamArr[ state.teamArr[doc.data().team].parent ].name : "")
                  }
                  list_MostEngage.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                  if(user.user_type == 'leadership'){
                    if(state.organization1level[doc.data().team]){
                      list_MostEngageFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                    }
                  }else{
                    list_MostEngageFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                  }
                  let temp = _.clone(doc.data())
                  _.set(temp,'team', temp_name);
                  _.set(temp,'team_name', temp_name_parent);
                  _.set(temp,'score', doc.data().score.toFixed(1));
                  _.set(temp,'teamid', doc.data().team);
                  if(doc.data().parent){
                      if(list_MostEngage_root[doc.data().parent] === undefined){
                          list_MostEngage_root[doc.data().parent] = new Array()
                      }
                      list_MostEngage_root[doc.data().parent].push(temp);
                  }
              });
              Object.entries(list_MostEngage_root).forEach(([key, value]) =>{ 
                  _.sortBy(value,'score')
                  value.reverse()
              });
              
              list_MostEngage.reverse()
              list_MostEngageFirstTime.reverse()
              commit('setMostEngageCheck', list_MostEngageFirstTime.slice(0, 2))
              commit('setMostEngageAll', list_MostEngage)
              commit('setMostEngageRoot', list_MostEngage_root)
            });
          }else{
            querySnapshot.forEach( (doc)=> {
              let temp_name = '';
              let temp_name_parent = '';
              if(state.teamArr[doc.data().team]){
              temp_name = state.teamArr[doc.data().team].name
              temp_name_parent = (state.teamArr[doc.data().team].parent != "" ? state.teamArr[ state.teamArr[doc.data().team].parent ].name : "")
              }
              list_MostEngage.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
              if(user.user_type == 'leadership'){
                if(state.organization1level[doc.data().team]){
                  list_MostEngageFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                }
              }else{
                list_MostEngageFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
              }
              let temp = _.clone(doc.data())
              _.set(temp,'team', temp_name);
              _.set(temp,'team_name', temp_name_parent);
              _.set(temp,'score', doc.data().score.toFixed(1));
              _.set(temp,'teamid', doc.data().team);
              if(doc.data().parent){
                  if(list_MostEngage_root[doc.data().parent] === undefined){
                      list_MostEngage_root[doc.data().parent] = new Array()
                  }
                  list_MostEngage_root[doc.data().parent].push(temp);
              }
            });
            Object.entries(list_MostEngage_root).forEach(([key, value]) =>{ 
                _.sortBy(value,'score')
                value.reverse()
            });
            
            list_MostEngage.reverse()
            list_MostEngageFirstTime.reverse()
            commit('setMostEngageCheck', list_MostEngageFirstTime.slice(0, 2))
            commit('setMostEngageAll', list_MostEngage)
            commit('setMostEngageRoot', list_MostEngage_root)
          }
        });
      },
      getRetentionRisks({commit,state}:any, user:any){
        const dateNow = new Date()
        let today = format(dateNow, 'YYYY-WW')
        firebase.firestore().collection('spotlight').where("type", "==",'retention_risks').where("date", "==",today).where("company", "==", user.company).orderBy('score', 'asc').get()
        .then((querySnapshot)=> {
            let list_RetentionRisks:Array<any> = []
            let list_RetentionRisksFirstTime:Array<any> = []
            let list_RetentionRisks_root:Array<any> = []
            if(querySnapshot.empty){
              let today2 = format(addDays(dateNow, -6), 'YYYY-WW')
              firebase.firestore().collection('spotlight').where("type", "==",'retention_risks').where("date", "==",today2).where("company", "==", user.company).orderBy('score', 'asc').get()
              .then((querySnapshot)=> {
                querySnapshot.forEach( (doc)=> {
                  let temp_name = '';
                  let temp_name_parent = '';
                  if(state.teamArr[doc.data().team]){
                  temp_name = state.teamArr[doc.data().team].name
                  temp_name_parent = (state.teamArr[doc.data().team].parent != "" ? state.teamArr[ state.teamArr[doc.data().team].parent ].name : "")
                  }
                  list_RetentionRisks.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                  if(user.user_type == 'leadership'){
                    if(state.organization1level[doc.data().team]){
                      list_RetentionRisksFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                    }
                  }else{
                    list_RetentionRisksFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                  }
                  let temp = _.clone(doc.data())
                  _.set(temp,'team', temp_name);
                  _.set(temp,'team_name', temp_name_parent);
                  _.set(temp,'score', doc.data().score.toFixed(1));
                  _.set(temp,'teamid', doc.data().team);
                  if(doc.data().parent){
                      if(list_RetentionRisks_root[doc.data().parent] === undefined){
                          list_RetentionRisks_root[doc.data().parent] = new Array()
                      }
                      list_RetentionRisks_root[doc.data().parent].push(temp);
                  }
                });
                Object.entries(list_RetentionRisks_root).forEach(([key, value]) =>{ 
                    _.sortBy(value,'score')
                });
                commit('setRetentionRisksCheck', list_RetentionRisksFirstTime.slice(0, 2))
                commit('setRetentionRisksAll', list_RetentionRisks)
                commit('setRetentionRisksRoot', list_RetentionRisks_root)
              });
            }else{
              querySnapshot.forEach( (doc)=> {
                let temp_name = '';
                let temp_name_parent = '';
                if(state.teamArr[doc.data().team]){
                temp_name = state.teamArr[doc.data().team].name
                temp_name_parent = (state.teamArr[doc.data().team].parent != "" ? state.teamArr[ state.teamArr[doc.data().team].parent ].name : "")
                }
                list_RetentionRisks.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                if(user.user_type == 'leadership'){
                  if(state.organization1level[doc.data().team]){
                    list_RetentionRisksFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                  }
                }else{
                  list_RetentionRisksFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                }
                let temp = _.clone(doc.data())
                _.set(temp,'team', temp_name);
                _.set(temp,'team_name', temp_name_parent);
                _.set(temp,'score', doc.data().score.toFixed(1));
                _.set(temp,'teamid', doc.data().team);
                if(doc.data().parent){
                    if(list_RetentionRisks_root[doc.data().parent] === undefined){
                        list_RetentionRisks_root[doc.data().parent] = new Array()
                    }
                    list_RetentionRisks_root[doc.data().parent].push(temp);
                }
              });
              Object.entries(list_RetentionRisks_root).forEach(([key, value]) =>{ 
                  _.sortBy(value,'score')
              });
              commit('setRetentionRisksCheck', list_RetentionRisksFirstTime.slice(0, 2))
              commit('setRetentionRisksAll', list_RetentionRisks)
              commit('setRetentionRisksRoot', list_RetentionRisks_root)
            }
        });
      },
      getMostImprove({commit,state}:any, user:any){
        const dateNow = new Date()
        //let today = moment().add(-7, 'days').tz("Asia/Bangkok").format('YYYY-WW')
        // let today = format(addDays(dateNow, -6), 'YYYY-WW')
        let today = format(dateNow, 'YYYY-WW')
        firebase.firestore().collection('spotlight').where("type", "==",'most_improved').where("date", "==",today).where("company", "==", user.company).orderBy('score', 'asc').get()
        .then((querySnapshot)=> {
            let list_MostImprove:Array<any> = []
            let list_MostImproveFirstTime:Array<any> = []
            let list_MostImprove_root:Array<any> = []
            let list_MostCritical:Array<any> = []
            let list_MostCriticalFirstTime:Array<any> = []
            let list_MostCritical_root:Array<any> = []
            if(querySnapshot.empty){
              let today2 = format(addDays(dateNow, -6), 'YYYY-WW')
              firebase.firestore().collection('spotlight').where("type", "==",'most_improved').where("date", "==",today2).where("company", "==", user.company).orderBy('score', 'asc').get()
              .then((querySnapshot)=> {
                querySnapshot.forEach( (doc)=> {
                  // console.log(doc.data())
                  if(doc.data().status == 'none'){
                      let temp_name = '';
                      let temp_name_parent = '';
                      if(state.teamArr[doc.data().team]){
                      temp_name = state.teamArr[doc.data().team].name
                      temp_name_parent = (state.teamArr[doc.data().team].parent != "" ? state.teamArr[ state.teamArr[doc.data().team].parent ].name : "")
                      }
                      list_MostImprove.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                      if(user.user_type == 'leadership'){
                        if(state.organization1level[doc.data().team]){
                          list_MostImproveFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                        }
                      }else{
                        list_MostImproveFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                      }
                      if(doc.data().scoreall && doc.data().scoreall[0] && doc.data().scoreall[0] < 0){
                        list_MostCritical.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().scoreall[0].toFixed(1) })
                        if(user.user_type == 'leadership'){
                          if(state.organization1level[doc.data().team]){
                            list_MostCriticalFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().scoreall[0].toFixed(1) })
                          }
                        }else{
                          list_MostCriticalFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().scoreall[0].toFixed(1) })
                        }
                      }
                      let temp = _.clone(doc.data())
                      _.set(temp,'team', temp_name);
                      _.set(temp,'team_name', temp_name_parent);
                      _.set(temp,'score', doc.data().score.toFixed(1));
                      _.set(temp,'teamid', doc.data().team);
                      let temp2 = _.clone(temp)
                      if(doc.data().parent){
                          if(list_MostImprove_root[doc.data().parent] === undefined){
                              list_MostImprove_root[doc.data().parent] = new Array()
                              list_MostCritical_root[doc.data().parent] = new Array()
                          }
                          list_MostImprove_root[doc.data().parent].push(temp);
                          list_MostCritical_root[doc.data().parent].push(temp2);
                      }
                    }
                  });
                  // sort score in list_MostEngage_root
                  Object.entries(list_MostImprove_root).forEach(([key, value]) =>{ 
                      _.sortBy(value,'score')
                      value.reverse()
                  });
                  list_MostImprove.reverse()
                  list_MostImproveFirstTime.reverse()
                  commit('setMostImproveCheck', list_MostImproveFirstTime.slice(0, 2))
                  commit('setMostImproveAll', list_MostImprove)
                  commit('setMostImproveRoot', list_MostImprove_root)
                  commit('setMostCriticalCheck', list_MostCriticalFirstTime.slice(0, 2))
                  commit('setMostCriticalAll', list_MostCritical)
                  commit('setMostCriticalRoot', list_MostCritical_root)
              });
            }else{
              querySnapshot.forEach( (doc)=> {
                // console.log(doc.data())
                if(doc.data().status == 'none'){
                    let temp_name = '';
                    let temp_name_parent = '';
                    if(state.teamArr[doc.data().team]){
                    temp_name = state.teamArr[doc.data().team].name
                    temp_name_parent = (state.teamArr[doc.data().team].parent != "" ? state.teamArr[ state.teamArr[doc.data().team].parent ].name : "")
                    }
                    list_MostImprove.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                    if(user.user_type == 'leadership'){
                      if(state.organization1level[doc.data().team]){
                        list_MostImproveFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                      }
                    }else{
                      list_MostImproveFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().score.toFixed(1) })
                    }
                    if(doc.data().scoreall && doc.data().scoreall[0] && doc.data().scoreall[0] < 0){
                      list_MostCritical.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().scoreall[0].toFixed(1) })
                      if(user.user_type == 'leadership'){
                        if(state.organization1level[doc.data().team]){
                          list_MostCriticalFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().scoreall[0].toFixed(1) })
                        }
                      }else{
                        list_MostCriticalFirstTime.push({'teamid':doc.data().team,'team':temp_name,'team_name':temp_name_parent,'score':doc.data().scoreall[0].toFixed(1) })
                      }
                    }
                    let temp = _.clone(doc.data())
                    _.set(temp,'team', temp_name);
                    _.set(temp,'team_name', temp_name_parent);
                    _.set(temp,'score', doc.data().score.toFixed(1));
                    _.set(temp,'teamid', doc.data().team);
                    let temp2 = _.clone(temp)
                    if(doc.data().parent){
                        if(list_MostImprove_root[doc.data().parent] === undefined){
                            list_MostImprove_root[doc.data().parent] = new Array()
                            list_MostCritical_root[doc.data().parent] = new Array()
                        }
                        list_MostImprove_root[doc.data().parent].push(temp);
                        list_MostCritical_root[doc.data().parent].push(temp2);
                    }
                }
              });
              // sort score in list_MostEngage_root
              Object.entries(list_MostImprove_root).forEach(([key, value]) =>{ 
                  _.sortBy(value,'score')
                  value.reverse()
              });
              list_MostImprove.reverse()
              list_MostImproveFirstTime.reverse()
              commit('setMostImproveCheck', list_MostImproveFirstTime.slice(0, 2))
              commit('setMostImproveAll', list_MostImprove)
              commit('setMostImproveRoot', list_MostImprove_root)
              commit('setMostCriticalCheck', list_MostCriticalFirstTime.slice(0, 2))
              commit('setMostCriticalAll', list_MostCritical)
              commit('setMostCriticalRoot', list_MostCritical_root)
            }
        });
      },
      async getDataGraphExport({ commit, dispatch, rootState, state }: any, {table,activeWhere,filter, labeldate}) {
        console.time("Start")
        commit('setSnackbar', true)
        // const insight_teams: Array<any> = [];
        const allteam = (await firebase.database().ref('team').orderByChild("company").equalTo(rootState.authen.user.company).once('value')).val()
        let selectTeam = ""
        if(table == 'modal'){
          selectTeam = (!state.teammodal || state.teammodal == 'all') ? state.teamHeight : state.teammodal
        }else {
          selectTeam = (!state.team || state.team == 'all') ? state.teamHeight : state.team
        }
        // let selectTeam = (!state.team || state.team == 'all') ? state.teamHeight : state.team
        let selectMonth = state.selectmonth
        let startdate = ""
        let enddate = ""
        if(state.selectmonth.length > 0){
          startdate = state.selectmonth[0].startdate
          enddate = state.selectmonth[state.selectmonth.length - 1].enddate
        }
        var promise3 = new Promise(function(resolve, reject) {
          const data3: { [key: string]: firebase.firestore.DocumentData; } = {}
          firebase.database().ref().child('team').orderByChild('company').equalTo(`${rootState.authen.user.company}`).once('value', snapshot => {
            snapshot.forEach((doc):any => {
              const team = doc.val()
              team.key = doc.key
              team.value = doc.val().name
              if(team.sup === undefined){
                  team.sup = new Array()
              }
              let teamId = team.key
              let arrTeam = state.organizations[teamId];
              if(arrTeam){
                if (arrTeam.parent) {
                    if (data3[teamId] === undefined) {
                      data3[teamId] = new Array();
                    }
                    data3[teamId][teamId] = true;
                    data3[teamId] = globalfunc.getTeamOfParent(arrTeam.parent,data3[teamId]);
                }else{
                  data3[teamId] = new Array();
                  data3[teamId][teamId] = true;
                }
              }else{
                data3[teamId] = new Array();
                data3[teamId][teamId] = true;
              }
            });
            resolve(data3);
          });
        });
        var promise2 = new Promise(function(resolve, reject) {
          const data2: Array<any> = [];
          firebase.firestore().collection('insight_teams').where("quest_id", "==", '-Kgs61AHOnpJ3rfyQr6t').where("company", "==", `${rootState.authen.user.company}`).where("date", ">=", startdate).where("date", "<=", enddate).orderBy('date', 'asc').get()
          .then(function (querySnapshot) {
              querySnapshot.forEach(function (doc) {
                if (data2[doc.data().team] === undefined) {
                  data2[doc.data().team] = new Array();
                }
                data2[doc.data().team].push(doc.data())
              });
              resolve(data2);
          });
        });
        var promise1 = new Promise(function(resolve, reject) {
          const data1: Array<any> = [];
          firebase.firestore().collection('active_users_teams').where("date", ">=",activeWhere.start).where('company','==',rootState.authen.user.company).get()
          .then(function (querySnapshot) {
            querySnapshot.forEach(function (doc) {
              if (data1[doc.data().team] === undefined) {
                data1[doc.data().team] = new Array();
              }
              data1[doc.data().team][doc.data().date] = doc.data();
            });
            resolve(data1);
          });
        });
        Promise.all([promise1, promise2, promise3]).then((values)=> {
          const active:any = values[0]
          const insight_teams:any = values[1]
          const teamIn:any = values[2]
          const response: Array<any> = [];
          const teamforsort: Array<any> = [];
          Object.entries(teamIn[selectTeam]).forEach(([key, value]) =>{
            
            const happy: Array<any> = [];
            const risk: Array<any> = [];
            const activeuser: Array<any> = [];
            const alluser: Array<any> = [];
            // const activemonth: Array<any> = [];
            for (let m = 0; m < selectMonth.length; m++) {
              // const _tmp: { [key: string]: number } = {}
              // _tmp['sumhappy'] = 0
              // _tmp['sumrisk'] = 0
              // _tmp['rows'] = 0
              // _tmp['totalusers'] = 0;
              // _tmp['totalactive'] = 0;
              // console.log(_tmp)
              let _tmp:Array<any> = [];
              if (_tmp[key] === undefined) {
                _tmp[key] = new Array();
              }
              _tmp[key]['sumhappy'] = 0
              _tmp[key]['sumrisk'] = 0
              _tmp[key]['rows'] = 0
              _tmp[key]['totalusers'] = 0;
              _tmp[key]['totalactive'] = 0;
              if(insight_teams[key]){
                for (let index = 0; index < insight_teams[key].length; index++) {
                  const element = insight_teams[key][index];
                  if(activeWhere.type && activeWhere.type === 'weeks') {
                    if (element.date >= selectMonth[m]['startdate'] && element.date < selectMonth[m]['enddate']) {
                      _tmp[key]['sumhappy'] += element.happy_percent
                      _tmp[key]['sumrisk'] += element.risk_percent
                      _tmp[key]['rows'] += 1
                    }
                  }else{
                    if (element.date >= selectMonth[m]['startdate'] && element.date <= selectMonth[m]['enddate']) {
                      _tmp[key]['sumhappy'] += element.happy_percent
                      _tmp[key]['sumrisk'] += element.risk_percent
                      _tmp[key]['rows'] += 1
                    }
                  }
                }
              }
              // if(activeWhere.type && activeWhere.type === 'months' && state.team === 'all') {
              //   for(const x in activemonth){
              //     if (x >= selectMonth[m]['startdate'] && x <= selectMonth[m]['enddate']) {
              //       _tmp[key]['totalactive'] = (_tmp[key]['totalactive'] < activemonth[x].active_users) ? activemonth[x].active_users: _tmp[key]['totalactive'];
              //     }
              //   }
              //   for(const x in active[key]){
              //     if (x >= selectMonth[m]['startdate'] && x <= selectMonth[m]['enddate']) {
              //       _tmp[key]['totalusers'] = (_tmp[key]['totalusers'] < active[key][x].usersTotal) ? active[key][x].usersTotal: _tmp[key]['totalusers'];
              //     }
              //   }
              //   activeuser.push((_tmp[key]['totalactive'] > 0) ? Math.ceil(Number(_tmp[key]['totalactive']*100) / _tmp[key]['totalusers']) : 0)
              //   alluser.push(_tmp[key]['totalactive'])
              // }else 
              if(activeWhere.type && (activeWhere.type === 'weeks' || activeWhere.type === 'months' )) {
                let users = [];
                for(const x in active[key]){
                  if (x >= selectMonth[m]['startdate'] && x <= selectMonth[m]['enddate']) {
                    _tmp[key]['totalusers'] = (_tmp[key]['totalusers'] < active[key][x].usersTotal) ? active[key][x].usersTotal: _tmp[key]['totalusers'];
                    users = _.union(users,active[key][x].users);
                  }
                }
                activeuser.push((users.length > 0) ? Math.ceil(Number(users.length*100) / _tmp[key]['totalusers']) +'%' : 0 +'%')
                if(selectMonth[m]['startdate'] >= '2020-09-01'){
                  alluser.push(users.length)
                }else{
                  alluser.push('-')
                }
                // alluser.push(users.length)
              }else{
                if(active[key] && active[key][selectMonth[m]['startdate']]){
                  if(active[key][selectMonth[m]['startdate']].usersActive>0){
                    activeuser.push(Math.ceil((active[key][selectMonth[m]['startdate']].usersActive*100)/active[key][selectMonth[m]['startdate']].usersTotal)+'%')
                  }else{
                    activeuser.push(0+'%');
                  }
                  if(selectMonth[m]['startdate'] >= '2020-09-01'){
                    alluser.push(active[key][selectMonth[m]['startdate']].usersActive);
                  }else{
                    alluser.push('-')
                  }
                  // alluser.push(active[key][selectMonth[m]['startdate']].usersActive);
                }else{
                  activeuser.push(0+'%');
                  if(selectMonth[m]['startdate'] >= '2020-09-01'){
                    alluser.push(0);
                  }else{
                    alluser.push('-')
                  }
                  // alluser.push(0);
                }
              }
              happy.push((_tmp[key]['rows'] > 0) ? Math.ceil(_tmp[key]['sumhappy'] / _tmp[key]['rows']) +'%' : 0+'%')
              risk.push((_tmp[key]['rows'] > 0) ? Math.ceil(_tmp[key]['sumrisk'] / _tmp[key]['rows']) +'%' : 0+'%')
            }
            if (response[key] === undefined) {
              response[key] = new Array();
            }
            if(key == selectTeam){
              response[key]['order'] = 0
            }else{
              response[key]['order'] = 1
            }
            response[key]['team_name'] = (allteam[key]) ? allteam[key].name : ''
            response[key]['happy'] = happy
            response[key]['risk'] = risk
            response[key]['activeuser']= activeuser
            response[key]['alluser']= alluser
            teamforsort.push({order:response[key]['order'],team_key:key , team_name: response[key]['team_name'] })

          });
          dispatch('genDataExport', {labeldate, filter,dataexportall: response , teamorder: _.orderBy(teamforsort, ["order","team_name"], ["asc","asc"])})
          console.timeEnd("Start")
        });
      },
      async genDataExport({ commit, state, dispatch, rootState }: any, obj:any) {
        const labeldate = obj.labeldate
        const filter = obj.filter
        const dataexportall = obj.dataexportall
        const teamMaster = obj.teamorder
        const Excel = require('exceljs');
        var wb = new Excel.Workbook();
        var ws = wb.addWorksheet('mySheet');

        /*TITLE*/
        ws.getRow(2).height = 20
        ws.getCell('B2').font = { name: 'Arial', size: 14, bold: true }
        ws.getCell('B2').alignment = { vertical: 'middle' }
        ws.getCell('B2').value = 'Team Insight for ' + rootState.authen.user.company_name

        ws.getRow(3).height = 20
        ws.getCell('B3').font = { name: 'Arial', size: 11 }
        ws.getCell('B3').alignment = { vertical: 'middle' }
        ws.getCell('B3').value = filter

        // /*Column headers*/
        ws.getColumn('A').width = 5;
        ws.getColumn('B').width = 25;
        ws.getColumn('C').width = 25;
        let header:any = ['','Team Name','']
        ws.getRow(5).values = _.concat(header,labeldate)
        
        /*Data*/
        _.forEach(teamMaster, (value, key)=> {
          let row1:any = ['']
          row1.push(value.team_name)
          row1.push('Happiness Rate')
          ws.addRow(_.concat(row1,dataexportall[value.team_key].happy))

          let row2:any = ['','']
          row2.push('Risk Rate')
          ws.addRow(_.concat(row2,dataexportall[value.team_key].risk))

          let row3:any = ['','']
          row3.push('Activity Rate')
          ws.addRow(_.concat(row3,dataexportall[value.team_key].activeuser))

          let row4:any = ['','']
          row4.push('Active Users')
          ws.addRow(_.concat(row4,dataexportall[value.team_key].alluser))
        });

        ws.eachRow(function (row) {
          if(row.number == 5){
            row.eachCell(function (cell) {
              if(cell.col == 2){
                  ws.getCell(cell.address).font = { name: 'Arial', size: 11, bold: true }
              }else if(cell.col > 3){
                ws.getCell(cell.address).font = { name: 'Arial', size: 11, bold: true }
                ws.getCell(cell.address).alignment = { horizontal: 'right' };
                ws.getColumn(cell.address.replace(/[0-9]/g, '')).width = 15;
              }
            });
          }else if(row.number > 5 ){
            row.eachCell(function (cell) {
              if(cell.col == 2){
                ws.getCell(cell.address).font = { name: 'Arial', size: 11 }
              }else if(cell.col == 3){
                ws.getCell(cell.address).font = { name: 'Arial', size: 10 }
              }else if(cell.col > 3){
                ws.getCell(cell.address).font = { name: 'Arial', size: 10 }
                ws.getCell(cell.address).alignment = { horizontal: 'right' };
              }
            });
          }
        });
        
        wb.xlsx.writeBuffer( {
          base64: true
        })
        .then( function (xls64) {
            // build anchor tag and attach file (works in chrome)
            var a = document.createElement("a");
            var data = new Blob([xls64], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

            var url = URL.createObjectURL(data);
            a.href = url;
            a.download = 'Team Insight' + ".xlsx";
            document.body.appendChild(a);
            a.click();
            setTimeout(function() {
                commit('setSnackbar', false)
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                },
                0);
        })
        .catch(function(error) {
            console.log(error.message);
        });
      },
      async getTeamEngagementScore({ commit, dispatch, rootState, state }: any, {selectTeam}) {
        commit('setLoadingDataEngagement',true)
        let dataChart = {};
        dataChart['team'] = [];
        dataChart['manager'] = [];
        dataChart['leader'] = [];
        dataChart['team_members'] = [];
        dataChart['manager_members'] = [];
        dataChart['leader_members'] = [];
        dataChart['labels'] = [];
        dataChart['newLabels'] = [];
        let dateNow = new Date()
        let startMonth = format(addMonths(new Date(), -5), 'YYYY-MM')
        let endMonth = format(dateNow, 'YYYY-MM-DD')
        startMonth = startMonth + '-01'
        let labelCheck:any = [];
        if(state.dataEngagementScoreHistoryByTeam && state.dataEngagementScoreHistoryByTeam[selectTeam]){
          commit('setChartEngagementScore', state.dataEngagementScoreHistoryByTeam[selectTeam]);
          commit('setLoadingDataEngagement', false);
        }else{
          if(Object.keys(state.dataMasterLabel).length == 0){
            let date = startMonth
            do {
              state.dataMasterLabel[date] = true
              date = format(addDays(new Date(date), 1), 'YYYY-MM-DD')
            }
            while (date < endMonth);
          }
          let dataMasterLabel = state.dataMasterLabel
          let dataByDate:any = []
          await firebase.firestore().collection('engagement_index_30day_rolling_team').where("company","==",rootState.authen.user.company).where("team", "==", selectTeam).where("date", ">=", startMonth).where("date", "<", endMonth).get()
            .then((querySnapshot) => {
            querySnapshot.forEach((doc) => {
              const data = doc.data()
              dataByDate[data.date] = data
            });
            for(const x in dataMasterLabel){
              const dateLabel = format(new Date(x), 'D MMMM YY')
              let Month = format(new Date(x), 'MMM')
              if(labelCheck[Month] == undefined){
                labelCheck[Month] = true
                dataChart['labels'].push(Month)
              }else{
                dataChart['labels'].push('')
              }
              if(dataByDate[x]){
                dataChart['team'].push((dataByDate[x].team_DEBI) ? Number(dataByDate[x].team_DEBI.toFixed(0)) : 0)
                dataChart['manager'].push((dataByDate[x].manager_DEBI) ? Number(dataByDate[x].manager_DEBI.toFixed(0)) : 0)
                dataChart['leader'].push((dataByDate[x].leader_DEBI) ? Number(dataByDate[x].leader_DEBI.toFixed(0)) : 0)
                dataChart['team_members'].push((dataByDate[x].team_members) ? dataByDate[x].team_members : 0)
                dataChart['manager_members'].push((dataByDate[x].manager_members) ? dataByDate[x].manager_members : 0)
                dataChart['leader_members'].push((dataByDate[x].leader_members) ? dataByDate[x].leader_members : 0)
                
              }else{
                dataChart['team'].push(0)
                dataChart['manager'].push(0)
                dataChart['leader'].push(0)
                dataChart['team_members'].push(0)
                dataChart['manager_members'].push(0)
                dataChart['leader_members'].push(0)
              }
              dataChart['newLabels'].push(dateLabel)
            }
            state.dataEngagementScoreHistoryByTeam[selectTeam] = dataChart
            commit('setChartEngagementScore', dataChart);
            commit('setLoadingDataEngagement', false);
          });
        }
      },
      async getTeamEngagementScoreModal({ commit, dispatch, rootState, state }: any, {selectTeam}) {
        commit('setLoadingDataEngagementModal',true)
        let dataChart = {};
        dataChart['team'] = [];
        dataChart['manager'] = [];
        dataChart['leader'] = [];
        dataChart['team_members'] = [];
        dataChart['manager_members'] = [];
        dataChart['leader_members'] = [];
        dataChart['labels'] = [];
        dataChart['newLabels'] = [];
        let dateNow = new Date()
        let startMonth = format(addMonths(new Date(), -5), 'YYYY-MM')
        let endMonth = format(dateNow, 'YYYY-MM-DD')
        startMonth = startMonth + '-01'
        let labelCheck:any = [];
        if(state.dataEngagementScoreHistoryByTeam && state.dataEngagementScoreHistoryByTeam[selectTeam]){
          commit('setChartEngagementScoreModal', state.dataEngagementScoreHistoryByTeam[selectTeam]);
          commit('setLoadingDataEngagementModal', false);
        }else{
          if(Object.keys(state.dataMasterLabel).length == 0){
            let date = startMonth
            do {
              state.dataMasterLabel[date] = true
              date = format(addDays(new Date(date), 1), 'YYYY-MM-DD')
            }
            while (date < endMonth);
          }
          let dataMasterLabel = state.dataMasterLabel
          let dataByDate:any = []
          await firebase.firestore().collection('engagement_index_30day_rolling_team').where("company","==",rootState.authen.user.company).where("team", "==", selectTeam).where("date", ">=", startMonth).where("date", "<", endMonth).get()
            .then((querySnapshot) => {
            querySnapshot.forEach((doc) => {
              const data = doc.data()
              dataByDate[data.date] = data
            });
            for(const x in dataMasterLabel){
              const dateLabel = format(new Date(x), 'D MMMM YY')
              let Month = format(new Date(x), 'MMM')
              if(labelCheck[Month] == undefined){
                labelCheck[Month] = true
                dataChart['labels'].push(Month)
              }else{
                dataChart['labels'].push('')
              }
              if(dataByDate[x]){
                dataChart['team'].push((dataByDate[x].team_DEBI) ? Number(dataByDate[x].team_DEBI.toFixed(0)) : 0)
                dataChart['manager'].push((dataByDate[x].manager_DEBI) ? Number(dataByDate[x].manager_DEBI.toFixed(0)) : 0)
                dataChart['leader'].push((dataByDate[x].leader_DEBI) ? Number(dataByDate[x].leader_DEBI.toFixed(0)) : 0)
                dataChart['team_members'].push((dataByDate[x].team_members) ? dataByDate[x].team_members : 0)
                dataChart['manager_members'].push((dataByDate[x].manager_members) ? dataByDate[x].manager_members : 0)
                dataChart['leader_members'].push((dataByDate[x].leader_members) ? dataByDate[x].leader_members : 0)
              }else{
                dataChart['team'].push(0)
                dataChart['manager'].push(0)
                dataChart['leader'].push(0)
                dataChart['team_members'].push(0)
                dataChart['manager_members'].push(0)
                dataChart['leader_members'].push(0)
              }
              dataChart['newLabels'].push(dateLabel)
            }
            state.dataEngagementScoreHistoryByTeam[selectTeam] = dataChart
            commit('setChartEngagementScoreModal', dataChart);
            commit('setLoadingDataEngagementModal', false);
          });
        }
      },
      async fetchDimensionTrends({ commit, state }: any, { activeWhere, requestKey }: any) {
        commit('setLoadingDimensionTrends', true)
        commit('setDimensionTrendsModal', {})
        // questId: used to query insight_teams (historical)
        // categoryId: used to match insightteamsByComp / insight_teams_fix (current snapshot, same as Team Status)
        const DIMENSIONS = [
          { key: 'feedback', questId: '-L1QWPtZvax_KlJgpNxF', categoryId: '-KxS-JNn6sNeqTD0KE53' },
          { key: 'wellness', questId: '-L36rxTgLhZTL4mq4SV1', categoryId: '-KxS05m9KaS70Ai6ps93' },
          { key: 'manager', questId: '-L1Qug-PhtzVOR2Dr5q7', categoryId: '-KxS-e4O2--BrGiRWbEF' },
          { key: 'recognition', questId: '-L0J5FFsGQYVkfcGujWo', categoryId: '-KxRwgLlN0DpB0p7al7N' },
          { key: 'ambassadorship', questId: '-L2ycGiBDo3rDGjLNKwW', categoryId: '-KxRzbMWTgeqpsSbCo_6' },
          { key: 'colleagues', questId: '-Kgs549ClADmNQwoNbbG', categoryId: '-KxRyiIXeDebHreB12mM' },
          { key: 'personal', questId: '-Kgn7oxLPPWlbM_ZDc_h', categoryId: '-KxRdFigA6geGzv2zJHQ' },
          { key: 'alignment', questId: '-LtOL_iR1N-ev2b_8Tmg', categoryId: '-KxS15s4DhoKSrjs4D7k' },
          { key: 'satisfaction', questId: '-L1FmpFO-v8IZc_B5EB7', categoryId: '-KxRzHeGK7cuwKtAKQwF' },
        ]
        const groupByMonth = activeWhere.type === 'months'
        const getPeriodKey = (dateValue: string) => {
          if (groupByMonth) {
            return dateValue.substring(0, 7)
          }
          const date = new Date(dateValue + 'T00:00:00')
          return format(startOfWeek(date, { weekStartsOn: 1 }), 'YYYY-MM-DD')
        }
        const getPeriodLabel = (periodKey: string) => {
          const normalizedDate = groupByMonth
            ? new Date(periodKey + '-01T00:00:00')
            : new Date(periodKey + 'T00:00:00')
          return format(normalizedDate, groupByMonth ? 'MMM YY' : 'DD MMM')
        }
        const getColor = (score: number, storedColor?: string) => {
          if (storedColor === 'green' || storedColor === 'yellow' || storedColor === 'red') return storedColor
          if (score >= 70) return 'green'
          if (score >= 40) return 'yellow'
          return 'red'
        }
        try {
          const selectedTeam = (state.teammodal === 'all') ? state.teamHeightModal : state.teammodal
          const startDate = activeWhere.start
          const today = format(new Date(), 'YYYY-MM-DD')
          const currentPeriodKey = getPeriodKey(today)
          const currentPeriodLabel = getPeriodLabel(currentPeriodKey)

          // Build current scores from insightteamsByComp (insight_teams_fix — same source as Team Status)
          // Use only the selected team's own score so it matches the Team Status row
          const currentScores: { [dimKey: string]: { score: number, color: string, periodKey: string, label: string } } = {}
          const insightByComp: any[] = state.insightteamsByComp || []
          // Build a category→record lookup for the selected team in one pass
          const teamRecByCategory: { [catId: string]: any } = {}
          for (const rec of insightByComp) {
            if (rec.team === selectedTeam && rec.category && rec.percent != null) {
              teamRecByCategory[rec.category] = rec
            }
          }
          for (const dim of DIMENSIONS) {
            const rec = teamRecByCategory[dim.categoryId]
            if (rec) {
              const score = Math.round(rec.percent)
              currentScores[dim.key] = {
                score,
                color: getColor(score, rec.color),
                periodKey: currentPeriodKey,
                label: currentPeriodLabel
              }
            }
          }

          // Single Firestore query for all dimensions (9 quest_ids fit within 'in' limit of 10)
          const allQuestIds = DIMENSIONS.map(d => d.questId)
          const questIdToDimKey: { [qid: string]: string } = {}
          for (const dim of DIMENSIONS) { questIdToDimKey[dim.questId] = dim.key }

          const allRecords: any[] = []
          const snapshot = await firebase.firestore().collection('insight_teams')
            .where('quest_id', 'in', allQuestIds)
            .where('team', '==', selectedTeam)
            .where('date', '>=', startDate)
            .orderBy('date', 'asc')
            .get()
          snapshot.forEach((doc: any) => allRecords.push(doc.data()))

          // Group records by dimension, then by period
          const periodMaps: { [dimKey: string]: { [periodKey: string]: { score: number, color: string, label: string, periodKey: string, date: string } } } = {}
          for (const dim of DIMENSIONS) { periodMaps[dim.key] = {} }

          for (const r of allRecords) {
            if (!r.date || r.percent == null || !r.quest_id) continue
            const dimKey = questIdToDimKey[r.quest_id]
            if (!dimKey) continue
            const periodKey = getPeriodKey(r.date)
            const score = Math.round(r.percent)
            periodMaps[dimKey][periodKey] = {
              score,
              color: getColor(score, r.color),
              label: getPeriodLabel(periodKey),
              periodKey,
              date: r.date
            }
          }

          const result: any = {}
          for (const dim of DIMENSIONS) {
            const periodMap = periodMaps[dim.key]
            let points = Object.keys(periodMap)
              .sort()
              .map((periodKey) => periodMap[periodKey])

            const current = currentScores[dim.key]
            if (current !== undefined) {
              const currentPoint = {
                score: current.score,
                color: current.color,
                label: current.label,
                periodKey: current.periodKey,
                date: today
              }
              const existingIndex = points.findIndex((point) => point.periodKey === current.periodKey)
              if (existingIndex >= 0) {
                points.splice(existingIndex, 1, { ...points[existingIndex], ...currentPoint })
              } else {
                points.push(currentPoint)
              }
            }

            points = points.sort((a, b) => a.periodKey.localeCompare(b.periodKey)).slice(-6)
            const rangeLabel = points.length === 0
              ? ''
              : (points.length === 1 ? points[0].label : points[0].label + ' → ' + points[points.length - 1].label)

            result[dim.key] = { points, current: current || null, rangeLabel }
          }
          if (isLatestScopedRequest(state, 'modalChart', requestKey)) {
            commit('setDimensionTrendsModal', result)
          }
        } catch (e) {
          console.error('fetchDimensionTrends failed:', e)
        }
        if (isLatestScopedRequest(state, 'modalChart', requestKey)) {
          commit('setLoadingDimensionTrends', false)
        }
      },
    },
    getters: {
      insightteams: (state: any) => {
        return state.insight_teams
      },
      insightteamsModal: (state: any) => {
        return state.insight_teams_modal
      },
      datachart: (state: any) => {
        return state.datachart
      },
      datachartModal: (state: any) => {
        return state.datachartModal
      },
      overviewWeeklyActive: (state: any) => {
        return state.overviewWeeklyActive
      },
      overviewWeeklyHealth: (state: any) => {
        return state.overviewWeeklyHealth
      },
      datalist: (state: any) => {
        return state.datalist
      },
      datalistModal: (state: any) => {
        return state.datalistModal
      },
      mostengage: (state:any) => {
        return state.mostengage
      },
      mostengageModal: (state:any) => {
        return state.mostengageModal
      },
      retentionrisks: (state:any) => {
        return state.retentionrisks
      },
      retentionrisksModal: (state:any) => {
        return state.retentionrisksModal
      },
      mostimprove: (state:any) => {
        return state.mostimprove
      },
      mostimproveModal: (state:any) => {
        return state.mostimproveModal
      },
      mostcritical: (state:any) => {
        return state.mostcritical
      },
      mostcriticalModal: (state:any) => {
        return state.mostcriticalModal
      },
      showSnackbar: (state:any) => {
        return state.showSnackbar
      },
      teamArr: (state:any) => {
        return state.teamArr
      },
      teamAll: (state:any) => {
        return state.teamAll
      },
      organizations: (state:any) => {
        return state.organizations
      },
      dataListLoading: (state:any) => {
        return state.datalistallLoadingStatus
      },
      loadingData: (state:any) => {
        return state.loadingData
      },
      loadingDataModal: (state:any) => {
        return state.loadingDataModal
      },
      loadingDataEngagement: (state:any) => {
        return state.loadingDataEngagement
      },
      loadingDataEngagementModal: (state:any) => {
        return state.loadingDataEngagementModal
      },
      databarchartEngagementScore: (state: any) => {
        return state.databarchartEngagementScore
      },
      databarchartEngagementScoreModal: (state: any) => {
        return state.databarchartEngagementScoreModal
      },
      dimensionTrendsModal: (state: any) => {
        return state.dimensionTrendsModal
      },
      loadingDimensionTrends: (state: any) => {
        return state.loadingDimensionTrends
      },
    }
  }
